/**
 * Ops/QA — Match Lab: video + spatial + keyboard labeling + audit trail.
 *
 * W34-ADMIN-LAB Part 2. Admin-gated staff tool for inspecting a match
 * with video playback, spatial view, event timeline, and writing event
 * labels into the existing event_labels table via keyboard shortcuts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError, api } from '../lib/api'
import { NO_FRAME_RATE_NOTE, servedFps } from '../lib/frameRate'
import { CanvasController } from '../pages/match/canvasController'
import { TrackingChunks } from './trackingChunks'
import {
  MatchLabPitch, eventSeekFrame, type PitchEvent, type VideoDirections,
} from './MatchLabPitch'
import { CoachTacticsBoard } from './CoachTacticsBoard'
import { GameNotes } from './GameNotes'
import { ReviewSignOff } from './ReviewSignOff'
import { ReviewBadge, ReviewControls } from './MatchLabReview'
import { SCAN_MAX_GAP_S, Scorecard, type EventScores } from './MatchLabReviewMode'
import type { WizardLabel, ZoneCandidate } from './MatchLabWizard'
import { MatchScore, type GoalScore } from './MatchScore'
import StoredScore, { type Score } from '../pages/MatchScore'
import {
  LABEL_PASSES, LabelModePanel, PASS_WRITE_TYPE, buildLabelQueue, movedLabel,
  candidateCount, hasFrameToWrite, labelName, nextOpen, readChecked, readDismissed,
  writeChecked, writeDismissed,
  type LabelItem, type LabelPass, type QueueInputs, type Scope,
} from './MatchLabLabelMode'
import type { GameData } from '../pages/match/types'
import {
  detectFramesIncludeGap, frameToVideoTime, syncFieldsOf, videoTimeToFrame,
  type SyncState,
} from '../lib/viz/frameTime'
import '../pages/admin.css'

// ── types ──

type GameListItem = {
  game_id: number
  game_code?: string | null
  home_team: string | null
  away_team: string | null
  match_date: string | null
  vendors: string[]
  organization_id?: number | null
}

type OpsOrganization = {
  id: number
  name: string
  code: string
}

type Period = {
  period_id: number
  start_frame_idx: number
  end_frame_idx: number
  home_attacks_positive_x: boolean
}

type TimelineEvent = {
  frame_idx: number
  team_id: number | null
  type: 'goal' | 'shot' | 'set_piece'
  label: string
}

/** A standing Match Lab review of a detected event (raw_assertion;
 *  app/layerstack/event_review.py). matched_by 'anchor': the event was
 *  rebuilt and the review found it again by kind, team and frame. */
export type EventReview = {
  assertion_id: number
  action: 'confirm' | 'reject' | 'correct'
  reason: string | null
  note: string | null
  correction: Record<string, string | number> | null
  user_id: number
  created_at: string
  matched_by?: 'id' | 'anchor'
}

type LabEvents = Pick<MatchLabInfo, 'goals' | 'shots' | 'set_pieces'>

type MatchLabInfo = {
  game_id: number
  game_code?: string | null
  home_team: string | null
  away_team: string | null
  org_side: string | null
  home_attack_dir_h1: string | null
  fps: number | null
  total_frames: number | null
  home_team_color: string | null
  away_team_color: string | null
  periods: Period[]
  // L4 positions: the acting team's attacking frame, metres (0-105 x
  // toward the goal it attacks, 0-68 y). A goal is placed at its shot.
  goals: { goal_id: number; frame_idx: number; team_id: number | null; goal_type: string | null
           x_att_m?: number | null; y_att_m?: number | null
           shot_team_id?: number | null
           review?: EventReview | null }[]
  shots: { action_id: number; frame_idx: number; team_id: number | null; outcome: string | null
           body_part?: string | null; x_att_m?: number | null; y_att_m?: number | null
           review?: EventReview | null }[]
  set_pieces?: { action_id: number; frame_idx: number; team_id: number | null
                 restart_type: string; x_att_m: number | null; y_att_m: number | null
                 review?: EventReview | null }[]
  teams: Record<string, number>
  // Video marks (seconds into the file) and the frame clock flag: the
  // frame <-> video-time mapping (lib/viz/frameTime, as Match Video).
  video_game_start_time?: number | null
  video_halftime_start_time?: number | null
  video_halftime_end_time?: number | null
  video_frame_aligned?: boolean
  video_offset_h1_s?: number | null
  video_offset_h2_s?: number | null
  video_time_map?: [number, number][] | null
  /** Per half, which way home attacks ON THE VIDEO (L3 camera view). */
  video_directions?: VideoDirections
}

type LabelTableEntry = {
  count: number
  recent: { id: number; frame: number; type?: string; side?: string | null; team_id?: number | null; created_at: string | null }[]
}

type LabelsPayload = {
  game_id: number
  tables: Record<string, LabelTableEntry>
  event_types: string[]
  goal_score?: GoalScore
  /** The score a person entered (game_facts), or null: services/match_score.
   *  (The API also says its source; the header has no use for it.) */
  stored_score?: { home: number; away: number } | null
}

type VideoPayload = {
  has_video: boolean
  video_url: string | null
  video_url_fallback?: string | null
  fps?: number | null
  total_frames?: number | null
}

// ── keyboard shortcut map ──

const SHORTCUTS: Record<string, { type: string; label: string; needsTeam: boolean }> = {
  p: { type: 'progressive_pass', label: 'Pass (progressive)', needsTeam: true },
  u: { type: 'supporting_pass', label: 'Pass (supportive)', needsTeam: true },
  c: { type: 'carry', label: 'Carry', needsTeam: true },
  g: { type: 'goal', label: 'Goal', needsTeam: true },
  h: { type: 'own_goal', label: 'Own goal (side = who it counts for)', needsTeam: true },
  // A plain shot states no outcome, so the build cannot add it as a shot
  // (l4/shots SHOT_LABEL_OUTCOMES); it is scored only. V / L / W add one.
  s: { type: 'shot', label: 'Shot, no outcome (scored only; V/L/W add it)', needsTeam: true },
  v: { type: 'shot_saved', label: 'Shot saved', needsTeam: true },
  l: { type: 'shot_blocked', label: 'Shot blocked', needsTeam: true },
  w: { type: 'shot_off_target', label: 'Shot wide / over', needsTeam: true },
  f: { type: 'foul', label: 'Foul / FK', needsTeam: true },
  m: { type: 'free_kick', label: 'Free kick taken', needsTeam: true },
  t: { type: 'throw_in', label: 'Throw-in', needsTeam: true },
  k: { type: 'corner', label: 'Corner kick', needsTeam: true },
  y: { type: 'goal_kick', label: 'Goal kick taken', needsTeam: true },
  n: { type: 'kick_off', label: 'Kick-off', needsTeam: true },
  o: { type: 'out_for_goal_kick', label: 'Goal kick (OOB)', needsTeam: false },
  r: { type: 'regain', label: 'Regain', needsTeam: true },
  b: { type: 'box_entry', label: 'Box entry', needsTeam: true },
  d: { type: 'duel', label: 'Duel', needsTeam: false },
  x: { type: 'offside', label: 'Offside', needsTeam: true },
}

const EVENT_TYPE_DESCRIPTIONS: Record<string, string> = {
  box_entry: 'Ball enters the attacking 18-yard box',
  carry: 'Player moves with ball under control',
  corner: 'Corner kick restart',
  duel: '1v1 contest for the ball (tackle, aerial, shoulder)',
  foul: 'Foul committed / free kick awarded',
  goal: 'Ball crosses the goal line — goal scored',
  own_goal: 'Own goal — side is the team it COUNTS FOR (the netting player\u2019s opponent)',
  goal_kick: 'Goal kick taken',
  offside: 'Offside infringement — play stopped',
  out_for_corner: 'Ball out of play, corner kick awarded',
  out_for_goal_kick: 'Ball out of play, goal kick awarded',
  progressive_pass: 'Progressive pass — moves ball toward opponent goal',
  regain: 'Ball recovered (interception, tackle, loose ball)',
  shot: 'Attempt on goal, outcome not stated',
  shot_blocked: 'Shot stopped by an outfield player',
  shot_off_target: 'Shot wide or over the bar',
  shot_saved: 'Shot stopped by the goalkeeper',
  free_kick: 'Dead-ball restart from a free kick',
  kick_off: 'Kick-off to start or restart play',
  throw_in: 'Throw-in restart',
  stop_for_free_kick: 'Play stopped for a free kick',
  stop_for_kick_off: 'Play stopped — awaiting kick-off',
  stop_for_throw_in: 'Play stopped — ball out for throw-in',
  supporting_pass: 'Supportive pass — retains possession laterally or backward',
}

const ALL_EVENT_TYPES = Object.keys(EVENT_TYPE_DESCRIPTIONS).sort()

/** An event code as the audit log shows it: 'goal' -> 'Goal',
 *  'stop_for_free_kick' -> 'Stop for free kick'. Anything that is not a
 *  lowercase code (a detected row's ready-made name, free text) passes
 *  through untouched. */
export const displayEventType = (code?: string): string | undefined =>
  code && /^[a-z0-9_]+$/.test(code)
    ? code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ')
    : code

// ── helpers ──

const fmt = (n: number | null | undefined) =>
  n == null ? '\u2014' : n.toLocaleString('en-US')

const DEFAULT_HOME_COLOR = '#C8303A'
const DEFAULT_AWAY_COLOR = '#4A7CBF'

/** How far INSIDE the halftime break playback must be before it skips to
 *  the second half, and how close to the break's end it stops skipping.
 *  Wide enough that a label AT the halftime whistle (video time ==
 *  videoHalftimeStartTime) plays without being yanked forward, and that
 *  the skip target (the break's end) is never itself inside the skip
 *  window. */
export const HALFTIME_SKIP_MARGIN_S = 1.0

/** Where playback should jump to skip the halftime break, or null to play
 *  on. Nothing in the break is labelable, and sitting through ~15 minutes
 *  of tunnel footage is how a labeling pass loses its operator. Only
 *  while PLAYING — a paused scrub into the break stays put — and only
 *  strictly inside it, so an event at the stroke of halftime still plays.
 *  A game without both marks has no known break to skip. */
export function halftimeSkipTarget(s: SyncState, t: number,
                                   paused: boolean): number | null {
  const vhs = s.videoHalftimeStartTime
  const vhe = s.videoHalftimeEndTime
  if (paused || vhs == null || vhe == null) return null
  return (t > vhs + HALFTIME_SKIP_MARGIN_S && t < vhe - HALFTIME_SKIP_MARGIN_S)
    ? vhe : null
}

/** The timeline bar's domain in frames. Tracking can end BEFORE the match
 *  does — LUFC@BHA's SecondSpectrum feed stops at 94:22, mid-stoppage —
 *  and a bar bounded by total_frames pegged the playhead at its right
 *  edge while play continued, and drew a stoppage-time label's dot past
 *  100% where overflow:hidden swallowed it. The domain follows whatever
 *  is real: the tracked frames, the playhead, and every labeled or
 *  detected event. The stretch beyond tracking is shaded by the bar
 *  (trackedFrames) so "no tracking here" stays visible. */
export function timelineDomain(trackedFrames: number, currentFrame: number,
                               eventFrames: number[]): number {
  return Math.max(trackedFrames, currentFrame, ...eventFrames, 0)
}


/** Does the page own this key press, or does the browser? Anything held
 *  with Cmd, Ctrl or Alt is the browser's (Cmd+Shift+R hard refresh,
 *  Cmd+R, Cmd+C, Ctrl+F5...): the label shortcuts used to take them, so a
 *  hard refresh was swallowed AND recorded a Regain label. Shift alone
 *  stays ours — Shift+letter labels the away team. */
export function pageOwnsKey(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey'>): boolean {
  return !(e.metaKey || e.ctrlKey || e.altKey)
}

/** Scan fast, then slow down on something interesting: the arrow keys
 *  drive the transport. Right plays forward fast, Up forward at normal
 *  speed, Left backward slowly, Down stops. */
export const TRANSPORT_FAST_RATE = 6
export const TRANSPORT_REVERSE_RATE = 0.5
export type Transport =
  | { kind: 'play'; rate: number }
  | { kind: 'reverse'; rate: number }
  | { kind: 'pause' }

export function transportForKey(key: string): Transport | null {
  switch (key) {
    case 'ArrowRight': return { kind: 'play', rate: TRANSPORT_FAST_RATE }
    case 'ArrowUp': return { kind: 'play', rate: 1 }
    case 'ArrowLeft': return { kind: 'reverse', rate: TRANSPORT_REVERSE_RATE }
    case 'ArrowDown': return { kind: 'pause' }
    default: return null
  }
}

/** Frame step: [ and ] step one frame, Shift ten. Read from e.code, as
 *  Shift turns the key into { and }. */
export function frameStepForKey(e: Pick<KeyboardEvent, 'code' | 'shiftKey'>): number | null {
  const dir = e.code === 'BracketLeft' ? -1 : e.code === 'BracketRight' ? 1 : 0
  return dir === 0 ? null : dir * (e.shiftKey ? 10 : 1)
}

/** A label is for a SIDE — event_labels.side 'home' / 'away' / NULL
 *  (migration 106; models/event_label.py) — while this page works in
 *  stack team ids. Labels cross between the two only here: sending the
 *  team id refused every away label, and read back, a side never matched
 *  a team. */
/** Where a game opens: its first half's start frame in video time, never
 *  before the synced kickoff. On a frame-aligned game frame N IS video
 *  time N / fps, and period 1 starting at frame 0 put the opening at 0:00
 *  of the file — Omaha v Bradley (233) kicks off at 3:17, BHA v LUFC
 *  (249) at 10:47. Otherwise frame 0 already maps to the kickoff. */
export function kickoffVideoTime(sync: SyncState, periodOneStart: number): number {
  return Math.max(frameToVideoTime(sync, periodOneStart),
                  sync.videoGameStartTime || 0)
}

export type LabelSide = 'home' | 'away'
export function toLabelSide(teamId: number | null | undefined,
                            home: number | null, away: number | null): LabelSide | null {
  if (teamId == null) return null
  return teamId === home ? 'home' : teamId === away ? 'away' : null
}
export function fromLabelSide(side: string | null | undefined,
                              home: number | null, away: number | null): number | null {
  return side === 'home' ? home : side === 'away' ? away : null
}


function frameToMinute(frame: number, fps: number, periods: Period[]): string {
  if (periods.length === 0) return `f${frame}`
  for (const p of periods) {
    if (frame >= p.start_frame_idx && frame <= p.end_frame_idx) {
      const elapsed = (frame - p.start_frame_idx) / fps
      const min = Math.floor(elapsed / 60)
      if (p.period_id === 1) return `${min}'`
      if (p.period_id === 2) return `${min + 45}'`
      return `P${p.period_id} ${min}'`
    }
  }
  return `f${frame}`
}

function frameToElapsed(frame: number, fps: number, periods: Period[]): string {
  if (periods.length === 0 || fps <= 0) return ''
  for (const p of periods) {
    if (frame >= p.start_frame_idx && frame <= p.end_frame_idx) {
      const elapsed = (frame - p.start_frame_idx) / fps
      const baseMin = p.period_id === 1 ? 0 : p.period_id === 2 ? 45 * 60 : (p.period_id - 1) * 45 * 60
      const totalSec = baseMin + elapsed
      const m = Math.floor(totalSec / 60)
      const s = Math.floor(totalSec % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    }
  }
  return ''
}

function videoTimeStr(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 100)
  return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
}

// ── main component ──

// ── Keyboard shortcuts panel ──
// A centred modal by default. Pinned, it becomes a small floating panel
// with no backdrop: it stays up while you label (keys keep working), can
// be dragged anywhere, and comes back pinned, in the same place, on the
// next visit (localStorage, this browser only).

const PIN_KEY = 'matchlab.shortcuts.pin'

type PinState = { pinned: boolean; x: number; y: number }

export function readPinned(): PinState {
  try {
    const v = JSON.parse(localStorage.getItem(PIN_KEY) ?? 'null') as PinState | null
    if (v && typeof v.pinned === 'boolean'
        && Number.isFinite(v.x) && Number.isFinite(v.y)) return v
  } catch { /* unreadable or private mode: defaults */ }
  return { pinned: false, x: 24, y: 96 }
}

function writePinned(v: PinState) {
  try { localStorage.setItem(PIN_KEY, JSON.stringify(v)) } catch { /* quota */ }
}

/** Keep a dragged panel's top-left on screen (the header stays reachable). */
function clampToViewport(x: number, y: number) {
  const maxX = Math.max(0, window.innerWidth - 120)
  const maxY = Math.max(0, window.innerHeight - 40)
  return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) }
}

const KBD: CSSProperties = {
  background: '#333', padding: '1px 6px', borderRadius: '3px',
  marginRight: '6px', minWidth: '20px', display: 'inline-block',
  textAlign: 'center',
}

function ShortcutList({ compact }: { compact: boolean }) {
  const heading = (first = false): CSSProperties => ({
    borderBottom: '1px solid #333', paddingBottom: '4px', marginBottom: '4px',
    marginTop: first ? 0 : '8px', gridColumn: '1 / -1', fontWeight: 600,
    fontSize: '11px', opacity: 0.5,
  })
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: compact ? '1px 14px' : '2px 24px',
                  fontSize: compact ? '11px' : '12px' }}>
      <div style={heading(true)}>PLAYBACK</div>
      <span><kbd style={KBD}>Space</kbd> Play / Pause</span>
      <span><kbd style={KBD}>{'\u2192'}</kbd> Play fast ({TRANSPORT_FAST_RATE}x)</span>
      <span><kbd style={KBD}>{'\u2191'}</kbd> Play 1x</span>
      <span><kbd style={KBD}>{'\u2190'}</kbd> Back slow ({TRANSPORT_REVERSE_RATE}x)</span>
      <span><kbd style={KBD}>{'\u2193'}</kbd> Stop</span>
      <span><kbd style={KBD}>[ ]</kbd> Frame step</span>
      <span><kbd style={KBD}>Shift+[ ]</kbd> 10 frames</span>
      <span><kbd style={KBD}>?</kbd> Show / hide</span>
      <div style={heading()}>REVIEW MODE</div>
      <span><kbd style={KBD}>Enter</kbd> Confirm, next</span>
      <span><kbd style={KBD}>{'\u232B'}</kbd> Reject, next</span>
      <span><kbd style={KBD}>E</kbd> Correct</span>
      <span><kbd style={KBD}>Q</kbd> Replay clip</span>
      <span><kbd style={KBD}>, .</kbd> Previous / next</span>
      <div style={heading()}>
        LABEL EVENTS (home team default, hold Shift for away)
      </div>
      {Object.entries(SHORTCUTS).map(([key, sc]) => (
        <span key={key} style={{ padding: '1px 0' }}
              title={EVENT_TYPE_DESCRIPTIONS[sc.type] ?? ''}>
          <kbd style={KBD}>{key.toUpperCase()}</kbd>
          {sc.label}
        </span>
      ))}
    </div>
  )
}

export function ShortcutsPanel({ onClose }: { onClose: () => void }) {
  const [pin, setPin] = useState<PinState>(readPinned)
  const drag = useRef<{ dx: number; dy: number } | null>(null)

  const update = useCallback((next: PinState) => {
    setPin(next)
    writePinned(next)
  }, [])

  // Dragging by the header. Pointer capture keeps the drag alive when the
  // pointer outruns the panel.
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pin.pinned || (e.target as HTMLElement).closest('button')) return
    drag.current = { dx: e.clientX - pin.x, dy: e.clientY - pin.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const { x, y } = clampToViewport(e.clientX - drag.current.dx,
                                     e.clientY - drag.current.dy)
    setPin((p) => ({ ...p, x, y }))
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    setPin((p) => { writePinned(p); return p })
  }

  // A window shrunk since last visit must not strand the panel off-screen.
  useEffect(() => {
    if (!pin.pinned) return
    const fit = () => setPin((p) => ({ ...p, ...clampToViewport(p.x, p.y) }))
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [pin.pinned])

  const close = () => {
    // Closing unpins: the next ? opens the modal again.
    if (pin.pinned) update({ ...pin, pinned: false })
    onClose()
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: '8px',
                  marginBottom: pin.pinned ? '6px' : '12px',
                  cursor: pin.pinned ? 'move' : 'default',
                  userSelect: 'none', touchAction: 'none' }}
         onPointerDown={onPointerDown} onPointerMove={onPointerMove}
         onPointerUp={onPointerUp}>
      <h3 style={{ margin: 0, fontSize: pin.pinned ? '12px' : '15px' }}>
        Keyboard Shortcuts
      </h3>
      <span style={{ display: 'flex', gap: '4px' }}>
        <button onClick={() => update({ ...pin, pinned: !pin.pinned })}
                aria-pressed={pin.pinned}
                title={pin.pinned
                  ? 'Unpin — back to a popup'
                  : 'Pin — keep on screen while you label; drag to move'}
                style={{ background: pin.pinned ? '#2563eb' : 'none',
                         border: '1px solid #444', color: pin.pinned ? '#fff' : '#aaa',
                         fontSize: '11px', cursor: 'pointer', padding: '1px 8px',
                         borderRadius: '3px' }}>
          {pin.pinned ? 'Pinned' : 'Pin'}
        </button>
        <button onClick={close} aria-label="Close shortcuts"
                style={{ background: 'none', border: 'none', color: '#888',
                         fontSize: pin.pinned ? '14px' : '18px',
                         cursor: 'pointer' }}>{'\u2715'}</button>
      </span>
    </div>
  )

  if (pin.pinned) {
    return (
      <div role="dialog" aria-label="Keyboard shortcuts (pinned)"
           style={{ position: 'fixed', left: pin.x, top: pin.y, zIndex: 1000,
                    background: 'rgba(26,26,30,0.94)', color: '#e0e0e0',
                    borderRadius: '6px', padding: '8px 12px', width: '360px',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
                    border: '1px solid #333' }}>
        {header}
        <ShortcutList compact />
      </div>
    )
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
                  background: 'rgba(0,0,0,0.5)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div role="dialog" aria-label="Keyboard shortcuts"
           style={{ background: '#1a1a1e', color: '#e0e0e0',
                    borderRadius: '8px', padding: '20px 28px',
                    maxWidth: '520px', width: '90%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
           onClick={(ev) => ev.stopPropagation()}>
        {header}
        <ShortcutList compact={false} />
      </div>
    </div>
  )
}

export default function MatchLab() {
  const { gameId: gameIdParam } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const gameId = (() => {
    const v = Number(gameIdParam)
    return Number.isInteger(v) && v > 0 ? v : null
  })()

  const [games, setGames] = useState<GameListItem[] | null>(null)
  const [orgs, setOrgs] = useState<OpsOrganization[]>([])
  const [clubFilter, setClubFilter] = useState('')
  const [selectedGameId, setSelectedGameId] = useState<number | null>(gameId)
  const [notesVersion, setNotesVersion] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // The selected game lives in the URL (/ops/match-lab/<id>), so the page
  // can be refreshed or the address pasted and open on the same match.
  // The route already read the param; selecting never wrote it back.
  // `replace`: stepping through games is one page, not a history trail.
  useEffect(() => {
    if (selectedGameId !== gameId) {
      navigate(selectedGameId == null
        ? '/ops/match-lab' : `/ops/match-lab/${selectedGameId}`,
      { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGameId])

  // LabView lifts info so the header can show team names
  const [labInfo, setLabInfo] = useState<MatchLabInfo | null>(null)
  const [goalScore, setGoalScore] = useState<GoalScore | null>(null)
  // The score clubs are shown (game_facts): staff accept the labeled one or
  // set their own here, and label the goals later (Ben, 2026-10-24).
  const [storedScore, setStoredScore] = useState<Score | null>(null)
  const onScores = useCallback((labeled: GoalScore | null, stored: Score | null) => {
    setGoalScore(labeled); setStoredScore(stored)
  }, [])

  useEffect(() => {
    api<{ games: GameListItem[] }>('/api/v1/opsqa/games')
      .then((d) => {
        setGames(d.games)
        if (selectedGameId == null && d.games[0]) {
          setSelectedGameId(d.games[0].game_id)
        }
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
    api<{ organizations: OpsOrganization[] }>('/api/v1/opsqa/organizations')
      .then((d) => setOrgs(d.organizations))
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredGames = useMemo(() => {
    if (!games) return null
    if (!clubFilter) return games
    const orgId = Number(clubFilter)
    return games.filter((g) => g.organization_id === orgId)
  }, [games, clubFilter])

  const homeColor = labInfo?.home_team_color ?? DEFAULT_HOME_COLOR
  const awayColor = labInfo?.away_team_color ?? DEFAULT_AWAY_COLOR

  return (
    <>
      {/* ── HEADER: title + teams + label dropdown + tabs ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', flexWrap: 'wrap',
                    marginBottom: '4px', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Match Lab</h1>
          {gameId != null && (
            <a href={`/ops/tracking-qa/${gameId}`} style={{ fontSize: '13px' }}
               title="Video beside tracking, with the QA detectors">
              Tracking QA</a>
          )}
          {labInfo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                          fontSize: '14px' }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%',
                             background: homeColor, display: 'inline-block',
                             border: '1px solid rgba(0,0,0,0.15)' }} />
              <strong>{labInfo.home_team ?? '?'}</strong>
              <span style={{ opacity: 0.4, fontWeight: 600 }}>v</span>
              <span style={{ width: 12, height: 12, borderRadius: '50%',
                             background: awayColor, display: 'inline-block',
                             border: '1px solid rgba(0,0,0,0.15)' }} />
              <strong>{labInfo.away_team ?? '?'}</strong>
              <MatchScore labeled={goalScore} detected={labInfo.goals.length > 0 ? {
                home: labInfo.goals.filter(g => g.team_id === labInfo.teams?.home).length,
                away: labInfo.goals.filter(g => g.team_id === labInfo.teams?.away).length,
              } : null} />
              {storedScore && selectedGameId != null && (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px',
                               fontSize: '13px' }}
                      aria-label="Match score">
                  <span className="muted">· score</span>
                  {/* the labeled score is printed just before: not again */}
                  <StoredScore gameId={selectedGameId} score={storedScore} canEdit
                               labeled={goalScore} showGoals={false}
                               onSaved={setStoredScore} />
                </span>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column',
                      alignItems: 'flex-end', gap: '4px' }}>
          <div style={{ display: 'flex', gap: '2px' }}>
            <a href="/ops/layer-stack"
               style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 500,
                        borderRadius: '4px 4px 0 0', background: 'transparent',
                        color: '#888', textDecoration: 'none',
                        borderBottom: '2px solid transparent' }}>
              Matches
            </a>
            <Link to={selectedGameId
                ? `/ops/layer-stack?view=match&game=${selectedGameId}`
                : '/ops/layer-stack?view=match'}
               style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 500,
                        borderRadius: '4px 4px 0 0', background: 'transparent',
                        color: '#888', textDecoration: 'none',
                        borderBottom: '2px solid transparent' }}>
              Layer Stack
            </Link>
            <span
               style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600,
                        borderRadius: '4px 4px 0 0',
                        background: 'var(--color-surface-raised, #f0f0f0)',
                        color: 'var(--color-text-primary, #333)',
                        borderBottom: '2px solid var(--color-text-primary, #333)' }}>
              Match Lab
            </span>
            <a href="/ops/layer-stack?view=docs"
               style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 500,
                        borderRadius: '4px 4px 0 0', background: 'transparent',
                        color: '#888', textDecoration: 'none',
                        borderBottom: '2px solid transparent' }}>
              Docs
            </a>
          </div>
          {games && games.length > 0 && (
            <div style={{ display: 'flex', gap: '4px' }}>
            {orgs.length > 1 && (
              <select className="adm-select"
                      value={clubFilter}
                      style={{ minWidth: '140px' }}
                      onChange={(e) => {
                        setClubFilter(e.target.value)
                        // Clear the game: leaving the old (now filtered
                        // out) id as the select's value made the browser
                        // DISPLAY the first option while state still held
                        // the old game — so choosing that first option
                        // changed nothing and it could not be loaded
                        // without bouncing through another one.
                        setSelectedGameId(null)
                        setLabInfo(null)
                        setGoalScore(null)
                        setStoredScore(null)
                      }}>
                <option value="">All clubs</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
            <select className="adm-select" value={selectedGameId ?? ''}
                    style={{ minWidth: '280px' }}
                    onChange={(e) => setSelectedGameId(
                      e.target.value ? Number(e.target.value) : null)}>
              {selectedGameId == null && (
                <option value="">Select a game{'…'}</option>
              )}
              {(filteredGames ?? games).map((g) => {
                // Prefer real names; fall back to game_code parts
                let home = g.home_team ?? '?'
                let away = g.away_team ?? '?'
                if (home.startsWith('game-') || home.startsWith('team-')) {
                  // Placeholder name from promote — derive from game_code if available
                  const gc = g.game_code
                  if (gc && gc.includes('@')) {
                    const [a, h] = gc.split('@')
                    home = h ?? home
                    away = a ?? away
                  }
                }
                return (
                  <option key={g.game_id} value={g.game_id}>
                    #{g.game_id} {home} v {away}
                    {g.match_date ? ` \u00b7 ${g.match_date}` : ''}
                  </option>
                )
              })}
            </select>
            </div>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {!error && games === null && <p className="muted">Loading...</p>}
      {!error && selectedGameId != null && (
        <>
          {/* A game with no recorded frame rate (match-lab-info fps null):
              the lab's events, clips and seeks are frames placed in time,
              so the note stands in place of the lab. */}
          {labInfo != null && labInfo.game_id === selectedGameId
            && servedFps(labInfo.fps) === null ? (
            <p className="muted" role="note">{NO_FRAME_RATE_NOTE}</p>
          ) : (
            <LabView gameId={selectedGameId} onInfoLoaded={setLabInfo}
                     onGoalScore={onScores} />
          )}
          {/* Signing a game off once it is reviewed (Ben, 2026-10-22): the
              same human-review record and staff notes as Match
              Administration, set here by button with a required comment
              (ReviewSignOff). key: a game switch remounts, never showing
              the last game's. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px',
                        marginTop: '12px' }}>
            <ReviewSignOff key={`rs-${selectedGameId}`} gameId={selectedGameId}
                           onChanged={() => setNotesVersion((v) => v + 1)} />
            {/* Remounted after a sign-off: outside Match Administration's
                action log nothing else tells it to reload. */}
            <GameNotes key={`gn-${selectedGameId}-${notesVersion}`} gameId={selectedGameId} />
          </div>
        </>
      )}
    </>
  )
}

// ── the main lab view for one game ──

type LabViewProps = {
  gameId: number
  onInfoLoaded: (info: MatchLabInfo | null) => void
  /** The goal labels' score and the stored one, for the header; again after
   *  every label write. Both null while a game loads. */
  onGoalScore: (score: GoalScore | null, stored: Score | null) => void
}

/** Loads match-lab-info, then mounts the lab at the game's recorded rate.
 *  Until the rate is known nothing is placed in time: the lab's clocks,
 *  seeks and canvas are built only from a real rate. A game whose rate is
 *  null never gets past here (the parent shows the note instead). */
function LabView(props: LabViewProps) {
  const { gameId, onInfoLoaded } = props
  const [info, setInfo] = useState<MatchLabInfo | null>(null)
  useEffect(() => {
    setInfo(null); onInfoLoaded(null)
    api<MatchLabInfo>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
      .then((d) => { setInfo(d); onInfoLoaded(d) })
      .catch(() => {})
  }, [gameId, onInfoLoaded])
  const fps = servedFps(info?.fps)
  if (info == null || info.game_id !== gameId || fps === null) {
    return <p className="muted">Loading...</p>
  }
  return <LoadedLabView key={gameId} {...props} info={info} fps={fps} />
}

function LoadedLabView({ gameId, onGoalScore, info, fps }: LabViewProps & {
  /** This game's match-lab-info, loaded. */
  info: MatchLabInfo
  /** The game's recorded frame rate (servedFps of info.fps). */
  fps: number
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoPaused, setVideoPaused] = useState(true)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [videoTime, setVideoTime] = useState(0)
  // Detected events live apart from info: a review refreshes them without
  // replacing info, which would rebuild the canvas controller.
  const [events, setEvents] = useState<LabEvents | null>(info)
  const [labels, setLabels] = useState<LabelsPayload | null>(null)
  const [labelsError, setLabelsError] = useState<string | null>(null)
  const [labelDropdownOpen, setLabelDropdownOpen] = useState(false)
  const [lastLabel, setLastLabel] = useState<string | null>(null)
  const [auditFilter, setAuditFilter] = useState<string>('')
  const [playbackRate, setPlaybackRate] = useState(1.0)
  const [spatialPopout, setSpatialPopout] = useState(false)
  const [tacticsOpen, setTacticsOpen] = useState(false)
  const spatialHostRef = useRef<HTMLDivElement>(null)
  const spatialColRef = useRef<HTMLDivElement>(null)
  const spatialWinRef = useRef<Window | null>(null)

  // Tracking frames for the spatial view, a minute per request: an R2
  // read costs ~6 s whatever its size (game 15, 2026-09-30: 300 frames
  // 6-7 s, 1,800 frames 10.7 s), so big chunks with one read ahead keep
  // 1x playback fed. Failures back off (trackingChunks.ts).
  const CHUNK = 1800
  const chunks = useMemo(() => new TrackingChunks(
    (start, count) => `/api/v1/opsqa/games/${gameId}/tracking-frames/${start}/${count}`,
    CHUNK), [gameId])

  const homeTeamId = info?.teams?.home ?? null
  const awayTeamId = info?.teams?.away ?? null
  const homeColor = info?.home_team_color ?? DEFAULT_HOME_COLOR
  const awayColor = info?.away_team_color ?? DEFAULT_AWAY_COLOR
  const totalFrames = info?.total_frames ?? 0

  useEffect(() => {
    setVideoUrl(null); setVideoError(null)
    api<VideoPayload>(`/api/v1/opsqa/games/${gameId}/video`)
      .then((d) => setVideoUrl(d.video_url ?? d.video_url_fallback ?? null))
      .catch((e: unknown) =>
        setVideoError(e instanceof ApiError ? e.message : String(e)))
  }, [gameId])

  // The game the page shows now: a labels answer for an earlier game,
  // arriving after a switch, must not put ITS score in the header (SDET #868).
  const shownGame = useRef(gameId)
  shownGame.current = gameId
  const loadLabels = useCallback(() => {
    setLabelsError(null)
    api<LabelsPayload>(`/api/v1/opsqa/games/${gameId}/labels`)
      .then((d) => {
        if (shownGame.current !== gameId) return
        setLabels(d)
        onGoalScore(d.goal_score ?? null, d.stored_score
          ? { home: d.stored_score.home, away: d.stored_score.away }
          : { home: null, away: null })
      })
      .catch((e: unknown) =>
        setLabelsError(e instanceof ApiError ? e.message : String(e)))
  }, [gameId, onGoalScore])

  useEffect(() => { onGoalScore(null, null) }, [gameId, onGoalScore])
  useEffect(() => { loadLabels() }, [loadLabels])

  // Frame <-> video time, the same mapping Match Video uses. Stack frames
  // count from the tracking clock, not the file: LAG@SKC kicks off 13 s
  // into its video, and treating frame 0 as 0:00 put every event 13 s
  // early. The frame convention (video-clock vs gapless) is measured from
  // this game's own second-half start, never assumed.
  const sync: SyncState = useMemo(() => {
    const base: SyncState = {
      videoGameStartTime: info?.video_game_start_time ?? 0,
      videoHalftimeStartTime: info?.video_halftime_start_time ?? null,
      videoHalftimeEndTime: info?.video_halftime_end_time ?? null,
      ...syncFieldsOf(info),
      fps,
    }
    const h2 = info?.periods?.find((p) => p.period_id === 2)
    return { ...base, framesIncludeHalftimeGap:
      detectFramesIncludeGap(base, h2?.start_frame_idx ?? null) }
  }, [info, fps])
  const liveSyncRef = useRef(sync)
  liveSyncRef.current = sync

  // Video events
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const onPlay = () => setVideoPaused(false)
    const onPause = () => setVideoPaused(true)
    const onTimeUpdate = () => {
      const skipTo = halftimeSkipTarget(
        liveSyncRef.current, video.currentTime, video.paused)
      if (skipTo != null) {
        video.currentTime = skipTo
        return
      }
      setVideoTime(video.currentTime)
      setCurrentFrame(videoTimeToFrame(liveSyncRef.current, video.currentTime))
    }
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('timeupdate', onTimeUpdate)
    return () => {
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('timeupdate', onTimeUpdate)
    }
  }, [videoUrl, fps])

  // CanvasController for the spatial pitch view — same as match-video
  const [controller, setController] = useState<CanvasController | null>(null)
  const syncRef = useRef<SyncState>(sync)
  const gameDataRef = useRef<GameData | null>(null)

  // Update sync/gameData refs from match-lab-info
  useEffect(() => {
    if (!info) return
    syncRef.current = sync
    gameDataRef.current = {
      home_team_start_direction: info.home_attack_dir_h1 ?? 'right',
      total_frames: info.total_frames ?? 0,
      fps,
      home_team_color: info.home_team_color,
      away_team_color: info.away_team_color,
    }
  }, [info, sync, fps])

  // Open each game at kickoff, not at 0:00 of the file (pre-match
  // footage). Once per game; a later seek is the user's.
  const openedAtKickoff = useRef<number | null>(null)
  useEffect(() => {
    const video = videoRef.current
    if (!video || !info || !videoUrl || openedAtKickoff.current === gameId) return
    const ko = info.periods.find((p) => p.period_id === 1)?.start_frame_idx ?? 0
    const go = () => {
      openedAtKickoff.current = gameId
      video.currentTime = kickoffVideoTime(sync, ko)
    }
    if (video.readyState >= 1) go()
    else {
      video.addEventListener('loadedmetadata', go, { once: true })
      return () => video.removeEventListener('loadedmetadata', go)
    }
  }, [info, videoUrl, gameId, sync])

  // Initialize CanvasController once info + game_code are available.
  // Delayed slightly so React has rendered the canvas and video elements
  // (CanvasController.init() uses getElementById on both).
  // We skip initializeTrackingCache() — layer-stack games don't have legacy
  // R2 parquets. Instead, we feed frames from our tracking-frames endpoint
  // via drawFrame() below.
  useEffect(() => {
    if (!info) return
    const gameCode = info.game_code ?? `${info.away_team ?? '?'}@${info.home_team ?? '?'}`

    const timer = setTimeout(() => {
      const c = new CanvasController({
        gameCode,
        currentUserId: 0,
        getSync: () => syncRef.current,
        getGameData: () => gameDataRef.current,
        getViewingAsTeam: () => 1,
        // no match-summary overlays here: pitch nearly edge to edge,
        // the same size as the event map beside it
        fieldMargins: { left: 6, right: 6, top: 6, bottom: 6 },
      })
      c.init()
      setController(c)
    }, 100)

    return () => {
      clearTimeout(timer)
      setController((prev) => { prev?.destroy(); return null })
    }
  }, [info])

  // ── Spatial pop-out: a real browser window (drag it to another monitor).
  // The live spatial column — canvas and all — is MOVED into the window,
  // not copied, so the controller keeps drawing into the same element.
  // Two things do not cross documents and are rewired here: sizing (the
  // controller's ResizeObserver belongs to this window) and the pitch
  // click (React listens on this document only).
  const popClickRef = useRef<((e: MouseEvent) => void) | null>(null)

  /** Put the spatial column back in this page, whatever state it is in. */
  const bringSpatialHome = useCallback(() => {
    const col = spatialColRef.current
    const host = spatialHostRef.current
    if (popClickRef.current) {
      col?.querySelector('canvas')?.removeEventListener('click', popClickRef.current)
      popClickRef.current = null
    }
    if (col && host && col.parentNode !== host) host.appendChild(col)
  }, [])


  const dockSpatial = useCallback(() => {
    const w = spatialWinRef.current
    spatialWinRef.current = null
    bringSpatialHome()
    setSpatialPopout(false)
    if (w && !w.closed) w.close()
  }, [bringSpatialHome])

  const popOutSpatial = useCallback(() => {
    const col = spatialColRef.current
    if (!col) return
    const w = window.open('', `matchlab-spatial-${gameId}`,
                          'popup,width=1100,height=720')
    if (!w) {
      setLastLabel('ERROR: the browser blocked the pop-out window — allow pop-ups for this site')
      return
    }
    w.document.title = `Spatial view — ${info?.home_team ?? 'Home'} v ${info?.away_team ?? 'Away'}`
    w.document.body.style.cssText = 'margin:0;background:#1a1a1e;overflow:hidden'
    w.document.body.appendChild(col)
    spatialWinRef.current = w
    setSpatialPopout(true)
    const onClick = (e: MouseEvent) =>
      controller?.handlePitchClick(e.clientX, e.clientY)
    popClickRef.current = onClick
    col.querySelector('canvas')?.addEventListener('click', onClick)
    // Closing the window (or navigating it) brings the view home.
    w.addEventListener('pagehide', () => dockSpatial())
  }, [gameId, info, controller, dockSpatial])

  // Leaving the game or the page closes the window with it.
  useEffect(() => () => {
    const w = spatialWinRef.current
    spatialWinRef.current = null
    bringSpatialHome()
    setSpatialPopout(false)
    if (w && !w.closed) w.close()
  }, [gameId, bringSpatialHome])

  // ── Spatial render loop: streams like Match Video. Every animation
  // frame it reads the video's own clock, maps it to a tracking frame and
  // draws that frame from the cache — so the pitch moves at the video's
  // frame rate, not at timeupdate's ~4 Hz. It also owns the canvas size:
  // a resize (window, pop-out, dock) clears a canvas, and the loop redraws
  // it at once, playing or paused. It runs on the window that HOLDS the
  // canvas, so a popped-out view keeps streaming with this tab hidden.

  useEffect(() => {
    const video = videoRef.current
    const col = spatialColRef.current
    if (!controller || !video || !col) return
    let stopped = false
    let handle = 0
    let host: Window = window
    let drawn = -1
    let size = ''
    const tick = () => {
      if (stopped) return
      const canvas = col.querySelector('canvas')
      if (canvas) {
        const w = col.clientWidth
        const h = col.clientHeight
        if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
          canvas.width = w
          canvas.height = h
        }
        const f = videoTimeToFrame(liveSyncRef.current, video.currentTime)
        const key = `${canvas.width}x${canvas.height}`
        const hit = chunks.frames.get(f)
        if (hit && (f !== drawn || key !== size)) {
          controller.drawFrame({ frame: f, players: hit.players,
                                 ball: hit.ball, phase: 1, referees: [] })
          drawn = f
          size = key
        }
        // Never leave an old picture standing as if it were this moment
        // (a seek into an unloaded minute takes ~10 s from R2).
        const note = col.querySelector<HTMLElement>('[data-role="spatial-loading"]')
        if (note) {
          note.style.display = !hit && drawn !== f && !chunks.isLoaded(f)
            ? 'flex' : 'none'
        }
        chunks.ensure(f)
        // Read ahead only once this minute is in: the one the playhead
        // needs goes first.
        if (chunks.isLoaded(f)) chunks.ensure(f + CHUNK)
        // Keep the cache to a few minutes around the playhead.
        chunks.prune(f, 3)
      }
      host = col.ownerDocument.defaultView ?? window
      handle = host.requestAnimationFrame(tick)
    }
    handle = window.requestAnimationFrame(tick)
    return () => {
      stopped = true
      try { host.cancelAnimationFrame(handle) } catch { /* window closed */ }
    }
    // spatialPopout: restart on pop-out AND dock. A tick queued on the
    // pop-out window never fires once that window closes — the loop died
    // there and the docked view froze on its last frame.
  }, [controller, videoUrl, chunks, spatialPopout])

  const seekToFrame = useCallback((frame: number) => {
    const video = videoRef.current
    if (video) video.currentTime = Math.max(0, frameToVideoTime(sync, frame))
  }, [sync])

  const seekAndPlay = useCallback((frame: number) => {
    const video = videoRef.current
    if (video) {
      video.currentTime = Math.max(0, frameToVideoTime(sync, frame))
      video.play().catch(() => {})
    }
  }, [sync])

  // All label entries for the audit trail (flatten all tables' recent lists)
  // Filter out entries with null/zero frame or no event type
  const auditEntries = useMemo(() => {
    if (!labels) return []
    const entries: { id: number; frame: number; type?: string; side?: string | null; team_id?: number | null; notes?: string; created_at: string | null; table: string }[] = []
    for (const [table, entry] of Object.entries(labels.tables)) {
      for (const r of entry.recent) {
        // Skip entries with no useful info
        if (r.frame == null || r.frame === 0) continue
        if (!r.type && table !== 'event_labels') continue
        entries.push({ ...r, table,
                       team_id: fromLabelSide(r.side, homeTeamId, awayTeamId) })
      }
    }
    return entries.sort((a, b) => b.id - a.id) // newest first
  }, [labels, homeTeamId, awayTeamId])


  // L4-detected goals, shots and set pieces: the pitch plot and the
  // audit list's "Detected" rows.
  // A correction shows its corrected values everywhere; a rejection keeps
  // the event in the list (struck through) but off the timeline and map.
  const pitchEvents: PitchEvent[] = useMemo(() => {
    if (!events) return []
    const fix = (rv: EventReview | null | undefined) =>
      rv?.action === 'correct' ? (rv.correction ?? {}) : {}
    const words = (t: string | null | undefined) => t ? t.replace(/_/g, ' ') : ''
    return [
      ...events.goals.map((g): PitchEvent => {
        const c = fix(g.review)
        const type = (c.goal_type as string | undefined) ?? g.goal_type
        return {
          key: `goal-${g.goal_id}`, kind: 'goal', event_id: g.goal_id,
          frame_idx: (c.frame_idx as number | undefined) ?? g.frame_idx,
          team_id: (c.team_id as number | undefined) ?? g.team_id,
          x_att_m: g.x_att_m ?? null, y_att_m: g.y_att_m ?? null,
          frame_team_id: g.shot_team_id ?? null,
          outcome: 'goal', review: g.review ?? null, detail: type,
          label: `Goal${type ? ` (${words(type)})` : ''}` }
      }),
      ...events.shots.map((sh): PitchEvent => {
        const c = fix(sh.review)
        const outcome = (c.outcome as string | undefined) ?? sh.outcome
        return {
          key: `shot-${sh.action_id}`, kind: 'shot', event_id: sh.action_id,
          frame_idx: (c.frame_idx as number | undefined) ?? sh.frame_idx,
          team_id: (c.team_id as number | undefined) ?? sh.team_id,
          x_att_m: sh.x_att_m ?? null, y_att_m: sh.y_att_m ?? null,
          outcome, review: sh.review ?? null, detail: outcome,
          label: `Shot${outcome ? ` (${words(outcome)})` : ''}` }
      }),
      ...(events.set_pieces ?? []).map((sp): PitchEvent => {
        const c = fix(sp.review)
        const rt = (c.restart_type as string | undefined) ?? sp.restart_type
        return {
          key: `sp-${sp.action_id}`, kind: 'set_piece', event_id: sp.action_id,
          frame_idx: (c.frame_idx as number | undefined) ?? sp.frame_idx,
          team_id: (c.team_id as number | undefined) ?? sp.team_id,
          x_att_m: sp.x_att_m, y_att_m: sp.y_att_m,
          review: sp.review ?? null, detail: rt, label: displayEventType(rt)! }
      }),
    ].sort((a, b) => a.frame_idx - b.frame_idx)
  }, [events])
  const liveEvents = useMemo(
    () => pitchEvents.filter((e) => e.review?.action !== 'reject'),
    [pitchEvents])

  const timelineEvents: TimelineEvent[] = useMemo(() => {
    const evts: TimelineEvent[] = liveEvents.map((e) => ({
      frame_idx: e.frame_idx, team_id: e.team_id, type: e.kind,
      label: e.label }))
    // User-labeled events from audit trail
    const seen = new Set(evts.map((e) => `${e.frame_idx}-${e.type}`))
    for (const r of auditEntries) {
      const key = `${r.frame}-${r.type}`
      if (seen.has(key)) continue
      seen.add(key)
      evts.push({
        frame_idx: r.frame,
        team_id: r.team_id ?? null,
        type: r.type === 'goal' ? 'goal' : 'shot', // use 'shot' shape for all non-goal labels
        label: r.type ?? r.table,
      })
    }
    return evts.sort((a, b) => a.frame_idx - b.frame_idx)
  }, [liveEvents, auditEntries])

  // Record a review, then refresh the events (not info — see `events`).
  // The one inline review form open, if any: which event, which form.
  const [reviewing, setReviewing] =
    useState<{ key: string; mode: 'reject' | 'correct' } | null>(null)
  const reviewEvent = useCallback(async (e: PitchEvent,
      body: Record<string, unknown>): Promise<boolean> => {
    try {
      await api(`/api/v1/opsqa/games/${gameId}/event-reviews`, {
        method: 'POST',
        body: JSON.stringify({ kind: e.kind, event_id: e.event_id, ...body }),
      })
      const d = await api<MatchLabInfo>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
      setEvents(d)
      setReviewing(null)
      setLastLabel(`${e.label}: ${String(body.action)} recorded`)
      return true
    } catch (err) {
      setLastLabel(`ERROR: ${err instanceof ApiError ? err.message : String(err)}`)
      return false
    }
  }, [gameId])

  // ── Label review (MatchLabLabelMode.tsx): Review mode and the Wizard as
  // one queue per event type — detections, labels and (with "+ Find more")
  // ball-position candidates, one item per moment, a verdict from the
  // keyboard, then the next one. One clip runner and playItem drive it.
  const [labelOn, setLabelOn] = useState(false)
  const [labelPass, setLabelPass] = useState<LabelPass>('goals')
  const [scope, setScope] = useState<Scope>('existing')
  const [currentKey, setCurrentKey] = useState<string | null>(null)
  const [scan, setScan] = useState(true)
  const [scanRate, setScanRate] = useState(6)
  const [scores, setScores] = useState<EventScores | null>(null)
  const [scoresError, setScoresError] = useState<string | null>(null)
  // E on a label or candidate: pick the frame by stepping the video.
  const [itemEdit, setItemEdit] = useState(false)
  // C on a candidate flips its suggested team (nothing is stored yet).
  const [flip, setFlip] = useState<Record<string, boolean>>({})
  const [checked, setChecked] = useState<Set<number>>(() => readChecked(gameId))
  const [dismissed, setDismissed] =
    useState<Set<string>>(() => readDismissed(gameId))
  const [labelData, setLabelData] = useState<{
    available: boolean; zones: ZoneCandidate[]; labels: WizardLabel[]
  } | null>(null)
  const [labelError, setLabelError] = useState<string | null>(null)

  // A game switch is a fresh review: its items, checks, dismissals and
  // flips all belong to the previous game.
  useEffect(() => {
    setLabelOn(false)
    setCurrentKey(null)
    setItemEdit(false)
    setFlip({})
    setLabelData(null)
    setLabelError(null)
    setChecked(readChecked(gameId))
    setDismissed(readDismissed(gameId))
  }, [gameId])

  // Every label of the reviewed types, and the ball-position candidates,
  // once per game when the review opens. The page's own label list caps
  // at 500 per table (was 50 — too few even to LIST a labeled match:
  // it hid four goal labels the score header counted, 2026-10-26). Cleanup guard: a game
  // switch mid-flight must drop the OLD game's response, or Accept writes
  // labels at the previous match's frames (SDET #737).
  useEffect(() => {
    if (!labelOn || labelData) return
    let on = true
    api<{ available: boolean; goals: ZoneCandidate[]
          corners: ZoneCandidate[]; throw_ins: ZoneCandidate[]
          goal_kicks: ZoneCandidate[]; kick_offs?: ZoneCandidate[]
          labels: WizardLabel[] }>(
      `/api/v1/opsqa/games/${gameId}/wizard-candidates`)
      .then((d) => {
        if (!on) return
        setLabelError(null)
        setLabelData({ available: d.available, labels: d.labels,
                       zones: [...d.goals, ...d.corners, ...d.throw_ins,
                               ...d.goal_kicks, ...(d.kick_offs ?? [])] })
      })
      .catch((e: unknown) => {
        if (on) setLabelError(e instanceof ApiError ? e.message : String(e))
      })
    return () => { on = false }
  }, [labelOn, labelData, gameId])

  const queueInputs = useMemo((): QueueInputs => ({
    events: pitchEvents, labels: labelData?.labels ?? [],
    zones: labelData?.zones ?? [], periods: info?.periods ?? [], fps,
    homeTeamId, awayTeamId,
  }), [pitchEvents, labelData, info, fps, homeTeamId, awayTeamId])
  const queues = useMemo(() => Object.fromEntries(LABEL_PASSES.map((p) =>
    [p.id, buildLabelQueue(p.id, queueInputs, scope)],
  )) as Record<LabelPass, LabelItem[]>, [queueInputs, scope])
  const moreCounts = useMemo(() => Object.fromEntries(LABEL_PASSES.map((p) =>
    [p.id, candidateCount(p.id, queueInputs)],
  )) as Record<LabelPass, number>, [queueInputs])
  const queue = queues[labelPass]
  const currentItem = queue.find((q) => q.key === currentKey) ?? null
  // The side a candidate is labeled with: its suggestion ('home' when
  // there is none — C names the fix), flipped while C has been pressed an
  // odd number of times on it.
  const baseSide: LabelSide = currentItem?.suggestedSide ?? 'home'
  const itemSide: LabelSide = currentItem && flip[currentItem.key]
    ? (baseSide === 'home' ? 'away' : 'home') : baseSide

  const loadScores = useCallback(() => {
    setScores(null); setScoresError(null)
    api<{ scores: EventScores }>(`/api/v1/opsqa/games/${gameId}/event-scores`)
      .then((d) => setScores(d.scores))
      .catch((e: unknown) =>
        setScoresError(e instanceof ApiError ? e.message : String(e)))
  }, [gameId])

  // The clip being played: the runner below slows a scan to the clip's
  // speed at its start and pauses at its end, then lets go.
  const clipRef = useRef<{ start: number; end: number; rate: number
                           restore: number; scanning: boolean } | null>(null)
  useEffect(() => {
    if (!labelOn) { clipRef.current = null; return }
    let handle = 0
    const tick = () => {
      const v = videoRef.current
      const c = clipRef.current
      if (v && c && !v.paused) {
        const f = videoTimeToFrame(liveSyncRef.current, v.currentTime)
        if (c.scanning && f >= c.start) {
          c.scanning = false
          v.playbackRate = c.rate
        } else if (!c.scanning && f >= c.end) {
          v.pause()
          v.playbackRate = c.restore
          clipRef.current = null
        }
      }
      handle = requestAnimationFrame(tick)
    }
    handle = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(handle)
  }, [labelOn])

  /** Play `item`'s clip. viaScan: reach it by playing fast from here when
   *  it is ahead and near enough, rather than jumping. */
  const playItem = useCallback((
      item: Pick<LabelItem, 'clipStart' | 'clipEnd' | 'fast'>,
      viaScan: boolean) => {
    const v = videoRef.current
    if (!v) return
    const rate = item.fast ? scanRate : playbackRate
    const gap = item.clipStart - videoTimeToFrame(sync, v.currentTime)
    const scanning = viaScan && scan && gap > 0 && gap <= SCAN_MAX_GAP_S * fps
    clipRef.current = { start: item.clipStart, end: item.clipEnd, rate,
                        restore: playbackRate, scanning }
    if (!scanning) v.currentTime = Math.max(0, frameToVideoTime(sync, item.clipStart))
    v.playbackRate = scanning ? scanRate : rate
    v.play().catch(() => {})
  }, [sync, fps, scan, scanRate, playbackRate])

  /** The speed picker acts NOW on what is playing (Ben, 2026-10-22: it
   *  only set the next fast-forward, and read as doing nothing). The
   *  current clip plays out at it; the next clip starts at normal speed. */
  const changeScanRate = useCallback((r: number) => {
    setScanRate(r)
    const v = videoRef.current
    if (v && !v.paused) v.playbackRate = r
    const c = clipRef.current
    if (c && !c.scanning) c.rate = r
  }, [])

  const goTo = useCallback((item: LabelItem | null, viaScan = false) => {
    setReviewing(null)
    setItemEdit(false)
    setCurrentKey(item?.key ?? null)
    if (item) playItem(item, viaScan)
    else loadScores()
  }, [playItem, loadScores])

  const startPass = useCallback((p: LabelPass) => {
    setLabelPass(p)
    // Always the FIRST item: opening mid-match on a partly judged game
    // read as losing the start (Ben, 2026-10-15). Verdicts still advance
    // to the next open one.
    goTo(queues[p][0] ?? null)
  }, [queues, goTo])

  // Start stepping once the labels fetch SETTLES — success or error: a
  // failed fetch still leaves the detections to review (SDET #737). ONCE
  // per opening: keyed on currentKey alone, the label written by judging
  // a pass's LAST item (which ends with no current item) re-fired this and
  // jumped back to item 1 instead of leaving the scorecard up (SDET #854).
  const autoStarted = useRef(false)
  useEffect(() => {
    if (labelOn && (labelData || labelError) && !autoStarted.current) {
      autoStarted.current = true
      startPass(labelPass)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelOn, labelData, labelError])

  const step = useCallback((delta: number) => {
    const at = queue.findIndex((q) => q.key === currentKey)
    const next = queue[at + delta]
    if (next) goTo(next, delta > 0)
  }, [queue, currentKey, goTo])

  /** On to the next open item after `fromKey`, scanning when near. */
  const advance = useCallback((fromKey: string, c: Set<number>,
                               d: Set<string>) => {
    goTo(nextOpen(queue, fromKey, c, d), true)
  }, [queue, goTo])

  const labelFail = useCallback((e: unknown) => {
    setLastLabel(`ERROR: ${e instanceof ApiError ? e.message : String(e)}`)
  }, [])

  const markChecked = useCallback((id: number): Set<number> => {
    const next = new Set(checked)
    next.add(id)
    setChecked(next)
    writeChecked(gameId, next)
    return next
  }, [checked, gameId])

  /** A detection verdict (an event review); on success go on to the next
   *  open item (an undo stays put, to be judged again). */
  const verdict = useCallback(async (body: Record<string, unknown>) => {
    const item = currentItem
    if (!item?.detected) return
    if (await reviewEvent(item.detected, body) && body.action !== 'undo') {
      advance(item.key, checked, dismissed)
    }
  }, [currentItem, reviewEvent, advance, checked, dismissed])

  /** Write a label (the endpoint upserts on game/frame/type) and fold it
   *  into the review's own label list; returns its id. */
  const writeLabel = useCallback(async (frame: number, eventType: string,
                                        side: LabelSide | null) => {
    const d = await api<{ label: { id: number }; updated?: boolean }>(
      `/api/v1/opsqa/games/${gameId}/labels`,
      { method: 'POST', body: JSON.stringify({ frame, event_type: eventType, side }) })
    // By id, not append: a re-accept UPDATES the existing label.
    setLabelData((z) => z ? { ...z, labels: [
      ...z.labels.filter((l) => l.id !== d.label.id),
      { id: d.label.id, frame, event_type: eventType, side },
    ] } : z)
    setLastLabel(`${eventType} #${d.label.id} at f${frame} (${side ?? 'no team'})`
      + (d.updated ? ' — updated' : ''))
    return d.label.id
  }, [gameId])

  const dropLabel = useCallback(async (id: number) => {
    await api(`/api/v1/opsqa/games/${gameId}/labels/${id}`, { method: 'DELETE' })
    setLabelData((z) => z ? { ...z, labels: z.labels.filter((l) => l.id !== id) } : z)
  }, [gameId])

  /** Enter / A. */
  const accept = useCallback(async () => {
    const item = currentItem
    if (!item) return
    if (item.detected) {
      if (item.detected.review) advance(item.key, checked, dismissed)
      else await verdict({ action: 'confirm' })
      return
    }
    try {
      if (item.label) {
        const l = item.label
        if (itemEdit && currentFrame !== l.frame) {
          // Moving a label is a new label at the new frame, then the old
          // one deleted: the PATCH edits the side only, by design.
          const m = movedLabel(l, currentFrame)
          const id = await writeLabel(m.frame, m.event_type, m.side)
          if (id !== l.id) await dropLabel(l.id)
          loadLabels()
          advance(item.key, markChecked(id), dismissed)
        } else {
          advance(item.key, markChecked(l.id), dismissed)
        }
        return
      }
      const eventType = PASS_WRITE_TYPE[item.pass]
      if (!eventType) return
      if (!hasFrameToWrite(item, itemEdit, currentFrame)) {
        setLastLabel('Pause on the goal and press E first')
        return
      }
      const id = await writeLabel(itemEdit ? currentFrame : item.frame,
                                  eventType, itemSide)
      loadLabels()
      advance(item.key, markChecked(id), dismissed)
    } catch (e: unknown) {
      labelFail(e)
    }
  }, [currentItem, advance, checked, dismissed, verdict, itemEdit,
      currentFrame, writeLabel, dropLabel, loadLabels, markChecked,
      itemSide, labelFail])

  /** Backspace / R. */
  const reject = useCallback(async () => {
    const item = currentItem
    if (!item) return
    if (item.detected) {
      if (item.detected.review) {
        setLastLabel('Already reviewed: undo it first')
        return
      }
      if (item.label && !window.confirm(
        'A label says this happened. Reject the detection anyway?')) return
      await verdict({ action: 'reject', reason: 'wrong' })
      return
    }
    if (item.label) {
      if (!window.confirm(`Delete this ${labelName(item.label.event_type)} label?`)) return
      try {
        await dropLabel(item.label.id)
        loadLabels()
        advance(item.key, checked, dismissed)
      } catch (e: unknown) {
        labelFail(e)
      }
      return
    }
    const next = new Set(dismissed)
    next.add(item.key)
    setDismissed(next)
    writeDismissed(gameId, next)
    advance(item.key, checked, next)
  }, [currentItem, verdict, dropLabel, loadLabels, advance, checked,
      dismissed, gameId, labelFail])

  const setItemEditing = useCallback((on: boolean) => {
    setItemEdit(on)
    if (on) {
      // Stepping to the exact frame: the clip runner must not pause or
      // retime the video out from under the arrow keys.
      clipRef.current = null
      videoRef.current?.pause()
    }
  }, [])

  /** E. */
  const edit = useCallback(() => {
    const item = currentItem
    if (!item) return
    if (item.detected) {
      if (!item.detected.review) setReviewing({ key: item.key, mode: 'correct' })
      return
    }
    setItemEditing(!itemEdit)
  }, [currentItem, itemEdit, setItemEditing])

  /** C: the other team — a detection correction, a label's side, or a
   *  candidate's suggestion. */
  const flipTeam = useCallback(async () => {
    const item = currentItem
    if (!item) return
    if (item.detected) {
      if (item.detected.review) {
        setLastLabel('Already reviewed: undo it first')
        return
      }
      const other = item.detected.team_id === homeTeamId ? awayTeamId : homeTeamId
      if (other == null) return
      if (await reviewEvent(item.detected, { action: 'correct',
                                             correction: { team_id: other } })) {
        advance(item.key, checked, dismissed)
      }
      return
    }
    if (item.label) {
      const l = item.label
      const to: LabelSide = l.side === 'away' ? 'home' : 'away'
      try {
        await api(`/api/v1/opsqa/games/${gameId}/labels/${l.id}`,
                  { method: 'PATCH', body: JSON.stringify({ side: to }) })
        setLabelData((z) => z ? { ...z, labels: z.labels.map((x) =>
          x.id === l.id ? { ...x, side: to } : x) } : z)
        loadLabels()
      } catch (e: unknown) {
        labelFail(e)
      }
      return
    }
    setFlip((m) => ({ ...m, [item.key]: !m[item.key] }))
  }, [currentItem, homeTeamId, awayTeamId, reviewEvent, advance, checked,
      dismissed, gameId, loadLabels, labelFail])

  /** /: Existing <-> + Find more. Stays on the current item when it is in
   *  both; otherwise moves to the new scope's first open one. */
  const changeScope = useCallback((s: Scope) => {
    if (s === scope) return
    setScope(s)
    const q = buildLabelQueue(labelPass, queueInputs, s)
    if (currentKey == null || !q.some((i) => i.key === currentKey)) {
      goTo(nextOpen(q, null, checked, dismissed) ?? q[0] ?? null)
    }
  }, [scope, labelPass, queueInputs, currentKey, checked, dismissed, goTo])

  const toggleLabelMode = useCallback(() => {
    if (labelOn) {
      setLabelOn(false)
      setCurrentKey(null)
      setReviewing(null)
      setItemEdit(false)
      const v = videoRef.current
      if (v) v.playbackRate = playbackRate
      return
    }
    setLabelOn(true)
    loadScores()
    // Started here when the data is in; otherwise the effect above starts
    // it when the fetch settles.
    autoStarted.current = !!(labelData || labelError)
    if (labelData || labelError) startPass(labelPass)
  }, [labelOn, playbackRate, loadScores, labelData, labelError, startPass,
      labelPass])

  // The audit list: labels and detected events in one match-order log.
  // ONE display vocabulary: a label's event code renders and filters as
  // the same name the detected rows use — 'goal' and a detected goal are
  // both 'Goal'. Without this the type dropdown listed both spellings
  // (seen on prod after migration 122 lowercased the codes, 2026-10-26).
  type LogRow = { key: string; frame: number; type?: string
                  team_id?: number | null; notes?: string
                  source: 'label' | 'detected'; event?: PitchEvent
                  // set only for event_labels rows — the one table with
                  // reassign/delete endpoints behind it
                  labelId?: number; side?: string | null }
  const logRows: LogRow[] = useMemo(() => [
    ...auditEntries.map((r): LogRow => ({
      key: `label-${r.table}-${r.id}`, frame: r.frame,
      type: displayEventType(r.type),
      team_id: r.team_id, notes: r.notes, source: 'label',
      ...(r.table === 'event_labels'
        ? { labelId: r.id, side: r.side ?? null } : {}) })),
    ...pitchEvents.map((e): LogRow => ({
      key: e.key, frame: e.frame_idx, team_id: e.team_id,
      type: e.kind === 'goal' ? 'Goal' : e.kind === 'shot' ? 'Shot'
        : e.label,
      notes: e.kind === 'set_piece' ? undefined : e.label,
      source: 'detected', event: e })),
  ].sort((a, b) => a.frame - b.frame), [auditEntries, pitchEvents])
  const [auditSource, setAuditSource] =
    useState<'all' | 'label' | 'detected'>('all')
  const shownRows = logRows.filter((r) =>
    (auditSource === 'all' || r.source === auditSource)
    && (!auditFilter || r.type === auditFilter))

  // ── Quick label via API ──
  const quickLabel = useCallback((eventType: string, teamId?: number | null) => {
    const body: Record<string, unknown> = { frame: currentFrame, event_type: eventType }
    const side = toLabelSide(teamId, homeTeamId, awayTeamId)
    if (side != null) body.side = side
    api<{ label: { id: number } }>(
      `/api/v1/opsqa/games/${gameId}/labels`,
      { method: 'POST', body: JSON.stringify(body) })
      .then((d) => {
        setLastLabel(`${eventType} #${d.label.id} at f${currentFrame}`)
        loadLabels()
        setTimeout(() => setLastLabel(null), 3000)
      })
      .catch((e: unknown) => {
        setLastLabel(`ERROR: ${e instanceof ApiError ? e.message : String(e)}`)
      })
  }, [gameId, currentFrame, loadLabels, homeTeamId, awayTeamId])

  // ── Fix a mislabeled team / remove a label, from the audit rail ──
  // The shortcuts label the HOME team unless Shift is held, so the
  // likeliest mistake is the right event on the wrong team.
  const flipLabel = useCallback((labelId: number, to: LabelSide) => {
    api(`/api/v1/opsqa/games/${gameId}/labels/${labelId}`,
        { method: 'PATCH', body: JSON.stringify({ side: to }) })
      .then(() => loadLabels())
      .catch((e: unknown) => {
        setLastLabel(`ERROR: ${e instanceof ApiError ? e.message : String(e)}`)
      })
  }, [gameId, loadLabels])

  const removeLabel = useCallback((labelId: number) => {
    api(`/api/v1/opsqa/games/${gameId}/labels/${labelId}`,
        { method: 'DELETE' })
      .then(() => loadLabels())
      .catch((e: unknown) => {
        setLastLabel(`ERROR: ${e instanceof ApiError ? e.message : String(e)}`)
      })
  }, [gameId, loadLabels])

  // ── Arrow-key transport (transportForKey) ──
  // A <video> cannot play backward (a negative playbackRate is refused),
  // so reverse is driven here: the video stays paused and each animation
  // frame moves currentTime back by the elapsed time x rate, skipping a
  // tick while the previous seek is still loading.
  const reverseRef = useRef<{ rate: number; last: number } | null>(null)
  const stopReverse = useCallback(() => { reverseRef.current = null }, [])
  useEffect(() => {
    let handle = 0
    const tick = (now: number) => {
      const v = videoRef.current
      const r = reverseRef.current
      if (v && r) {
        if (!v.paused) reverseRef.current = null   // something pressed play
        else if (!v.seeking) {
          const back = ((now - r.last) / 1000) * r.rate
          r.last = now
          if (v.currentTime <= 0) reverseRef.current = null
          else v.currentTime = Math.max(0, v.currentTime - back)
        }
      }
      handle = requestAnimationFrame(tick)
    }
    handle = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(handle)
  }, [])

  const runTransport = useCallback((t: Transport) => {
    const v = videoRef.current
    if (!v) return
    // A key press takes over from a review clip: the clip runner must not
    // later slow, pause or restore the speed under it.
    clipRef.current = null
    stopReverse()
    if (t.kind === 'pause') { v.pause(); return }
    if (t.kind === 'reverse') {
      v.pause()
      reverseRef.current = { rate: t.rate, last: performance.now() }
      return
    }
    setPlaybackRate(t.rate)
    v.playbackRate = t.rate
    v.play().catch(() => {})
  }, [stopReverse])

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      // The Tactics Board is a modal and owns the keyboard while it is open;
      // otherwise Space/arrows would also play/pause and seek the video.
      if (tacticsOpen) return
      if (!pageOwnsKey(e)) return

      // ? = toggle shortcuts overlay
      if (e.key === '?') {
        e.preventDefault()
        setShowShortcuts((v) => !v)
        return
      }
      // Label review keys (MatchLabLabelMode.tsx). A/R/E/C SHADOW the c
      // (CARRY) and r (REGAIN) label shortcuts while it is open, as the
      // Wizard's did; the others — G, S, K, ... — still label, so a goal
      // spotted mid-review is one key. Scope is "/": F is Foul / FK.
      if (labelOn && e.key === 'Escape') {
        e.preventDefault()
        if (itemEdit) setItemEditing(false)
        else if (reviewing) setReviewing(null)
        else toggleLabelMode()
        return
      }
      // Enter / Backspace with nothing current (loading, or a pass done)
      // must not fall through to a focused button — the toolbar's Review
      // button, which would close the review.
      if (labelOn && !currentItem && (e.key === 'Enter' || e.key === 'Backspace')) {
        e.preventDefault()
        return
      }
      if (labelOn && e.key === '/') {
        e.preventDefault()
        changeScope(scope === 'existing' ? 'more' : 'existing')
        return
      }
      if (labelOn && currentItem && ['Enter', 'Backspace', 'a', 'A', 'r', 'R',
                                     'e', 'E', 'c', 'C', 'q', 'Q', ',', '.',
                                    ].includes(e.key)) {
        e.preventDefault()     // Enter must not also click a focused button
        if (e.repeat) return
        const k = e.key.toLowerCase()
        if (k === 'enter' || k === 'a') void accept()
        else if (k === 'backspace' || k === 'r') void reject()
        else if (k === 'e') edit()
        else if (k === 'c') void flipTeam()
        else if (k === 'q') playItem(currentItem, false)
        else step(k === '.' ? 1 : -1)
        return
      }
      // Space = play/pause
      if (e.key === ' ') {
        e.preventDefault()
        const video = videoRef.current
        // Space while rewinding stops, as Down does: the video is paused
        // under the reverse runner, so a plain toggle would start it.
        if (video && reverseRef.current) { stopReverse(); return }
        if (video) { if (video.paused) video.play(); else video.pause() }
        return
      }
      // Arrows drive the transport (transportForKey). A held arrow
      // auto-repeats the same command, which is harmless but skipped.
      const transport = transportForKey(e.key)
      if (transport) {
        e.preventDefault()
        if (!e.repeat) runTransport(transport)
        return
      }
      // [ ] = frame step, Shift = 10 frames.
      const frameStep = frameStepForKey(e)
      if (frameStep != null) {
        e.preventDefault()
        stopReverse()
        seekToFrame(Math.max(0, currentFrame + frameStep))
        return
      }

      // Label shortcuts
      const sc = SHORTCUTS[e.key.toLowerCase()]
      if (!sc) return
      e.preventDefault()
      // A held key auto-repeats: one press is one label, not a dozen.
      if (e.repeat) return

      if (sc.needsTeam && homeTeamId != null) {
        // 1 = home, 2 = away (pressed after the letter)
        // For now: default to home team, hold Shift for away
        const team = e.shiftKey ? awayTeamId : homeTeamId
        quickLabel(sc.type, team)
      } else {
        quickLabel(sc.type)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [tacticsOpen, currentFrame, homeTeamId, awayTeamId, seekToFrame, quickLabel,
      labelOn, currentItem, itemEdit, reviewing, scope, accept, reject, edit,
      flipTeam, playItem, step, setItemEditing, toggleLabelMode, changeScope,
      runTransport, stopReverse])

  // Keyboard shortcut overlay
  const [showShortcuts, setShowShortcuts] = useState(
    () => readPinned().pinned)

  const stepFrame = useCallback((delta: number) => {
    seekToFrame(Math.max(0, currentFrame + delta))
  }, [currentFrame, seekToFrame])

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (video) { if (video.paused) video.play(); else video.pause() }
  }, [])

  const changeSpeed = useCallback((rate: number) => {
    setPlaybackRate(rate)
    const video = videoRef.current
    if (video) video.playbackRate = rate
  }, [])

  return (
    <div>
      {/* ── Keyboard shortcuts: modal, or pinned floating panel ── */}
      {showShortcuts && (
        <ShortcutsPanel onClose={() => setShowShortcuts(false)} />
      )}

      {/* Coach's Tactics Board: this moment as a slide deck of diagrams. */}
      {tacticsOpen && (
        <CoachTacticsBoard
          gameId={gameId} startFrame={currentFrame} fps={fps}
          homeColor={homeColor} awayColor={awayColor}
          homeName={info?.home_team ?? 'Home'} awayName={info?.away_team ?? 'Away'}
          events={timelineEvents} onSeek={seekToFrame}
          onClose={() => setTacticsOpen(false)}
        />
      )}

      {labelOn && (
        <LabelModePanel
          pass={labelPass} onPass={startPass} scope={scope} onScope={changeScope}
          queues={queues} moreCounts={moreCounts}
          checked={checked} dismissed={dismissed} current={currentItem}
          editing={itemEdit} side={itemSide}
          onAccept={() => { void accept() }} onReject={() => { void reject() }}
          onEdit={setItemEditing} onFlip={() => { void flipTeam() }}
          onNext={() => step(1)} onPrev={() => step(-1)}
          onReplay={() => currentItem && playItem(currentItem, false)}
          onGo={(item) => goTo(item)} onExit={toggleLabelMode}
          scan={scan} onScan={setScan} scanRate={scanRate} onScanRate={changeScanRate}
          reviewMode={currentItem && reviewing?.key === currentItem.key
            ? reviewing.mode : null}
          onReviewMode={(m) => currentItem && setReviewing(
            m ? { key: currentItem.key, mode: m } : null)}
          onReviewSubmit={(body) => { void verdict(body) }}
          currentFrame={currentFrame}
          homeTeamId={homeTeamId} awayTeamId={awayTeamId}
          homeName={info?.home_team ?? 'Home'} awayName={info?.away_team ?? 'Away'}
          minute={(f) => frameToMinute(f, fps, info?.periods ?? [])}
          available={labelData?.available ?? true}
          loading={!labelData && !labelError}
          error={labelError}
          scorecard={<Scorecard scores={scores} error={scoresError} />}
        />
      )}

      {/* ── 1. Timeline with event markers (full width, top) ── */}
      {totalFrames > 0 && (
        <EventTimeline
          events={timelineEvents}
          periods={info?.periods ?? []}
          totalFrames={timelineDomain(
            totalFrames, currentFrame, timelineEvents.map((e) => e.frame_idx))}
          trackedFrames={totalFrames}
          currentFrame={currentFrame}
          fps={fps}
          homeColor={homeColor}
          awayColor={awayColor}
          homeTeamId={homeTeamId}
          onSeek={seekToFrame}
        />
      )}

      {/* ── 2. Transport bar: frame/time + controls ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '4px 12px',
                    background: '#1a1a1e', color: '#e0e0e0',
                    borderRadius: '0 0 4px 4px',
                    fontFamily: 'var(--font-mono, monospace)', fontSize: '12px',
                    fontVariantNumeric: 'tabular-nums', marginBottom: '4px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span>f<strong>{fmt(currentFrame)}</strong></span>
          <span>{videoTimeStr(videoTime)}</span>
          {info && info.periods.length > 0 && (
            <span style={{ fontWeight: 600 }}>{frameToMinute(currentFrame, fps, info.periods)}</span>
          )}
        </div>
        {/* Transport: skip by seconds (fps-aware), step by frame, play */}
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          {[30, 10, 1].map((s) => (
            <button key={`b${s}`} onClick={() => stepFrame(-Math.round(s * fps))}
                    title={`Back ${s}s`}
                    style={{ background: 'none', border: '1px solid #444',
                             color: '#ccc', fontSize: '10px', cursor: 'pointer',
                             padding: '2px 5px', borderRadius: '3px' }}>
              {'\u2039'}{s}s
            </button>
          ))}
          <button onClick={() => stepFrame(-1)} title="Previous frame"
                  style={{ background: 'none', border: 'none', color: '#ccc',
                           fontSize: '16px', cursor: 'pointer', padding: '2px 6px' }}>
            {'\u23F4'}
          </button>
          <button onClick={togglePlay} title={videoPaused ? 'Play' : 'Pause'}
                  style={{ background: 'none', border: 'none', color: '#fff',
                           fontSize: '20px', cursor: 'pointer', padding: '2px 8px' }}>
            {videoPaused ? '\u25B6' : '\u23F8'}
          </button>
          <button onClick={() => stepFrame(1)} title="Next frame"
                  style={{ background: 'none', border: 'none', color: '#ccc',
                           fontSize: '16px', cursor: 'pointer', padding: '2px 6px' }}>
            {'\u23F5'}
          </button>
          {[1, 10, 30].map((s) => (
            <button key={`f${s}`} onClick={() => stepFrame(Math.round(s * fps))}
                    title={`Forward ${s}s`}
                    style={{ background: 'none', border: '1px solid #444',
                             color: '#ccc', fontSize: '10px', cursor: 'pointer',
                             padding: '2px 5px', borderRadius: '3px' }}>
              {s}s{'\u203A'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', fontSize: '10px',
                      alignItems: 'center' }}>
          <select value={playbackRate}
                  onChange={(e) => changeSpeed(Number(e.target.value))}
                  style={{ background: '#333', color: '#ccc', border: '1px solid #555',
                           borderRadius: '3px', fontSize: '10px', padding: '1px 2px',
                           cursor: 'pointer' }}>
            {[0.25, 0.5, 0.9, 1.0, 1.1, 1.25, 1.5, 2.0, 3.0, 4.0,
              TRANSPORT_FAST_RATE].map((r) => (
              <option key={r} value={r}>{r === 1 ? '1x' : `${r}x`}</option>
            ))}
          </select>
          <span style={{ opacity: 0.5 }}>{fmt(fps)} fps</span>
          <button onClick={toggleLabelMode} aria-pressed={labelOn}
                  title="Review goals, shots, corners and restarts: what was detected and labeled, and (/) candidates to find more"
                  style={{ background: labelOn ? '#2563eb' : 'none',
                           border: '1px solid #444',
                           color: labelOn ? '#fff' : '#ccc', fontSize: '10px',
                           cursor: 'pointer', padding: '1px 6px', borderRadius: '3px' }}>
            Review
          </button>
          <button onClick={() => { setVideoPaused(true); videoRef.current?.pause();
                                   setTacticsOpen(true) }}
                  title="Turn this moment into a coach's slide deck of transition diagrams"
                  style={{ background: 'none', border: '1px solid #444',
                           color: '#ccc', fontSize: '10px', cursor: 'pointer',
                           padding: '1px 6px', borderRadius: '3px' }}>
            {'⚽'} Tactics Board
          </button>
          <button onClick={() => setShowShortcuts(true)}
                  style={{ background: 'none', border: '1px solid #444',
                           color: '#888', fontSize: '10px', cursor: 'pointer',
                           padding: '1px 6px', borderRadius: '3px' }}>
            ?
          </button>
        </div>
      </div>

      {/* ── 3. Main: video+spatial left, audit trail right ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px',
                    gap: '8px', alignItems: 'start' }}>

        {/* LEFT: video + spatial stacked */}
        <div>
          {videoError && !videoUrl && (
            <p className="muted" style={{ margin: '8px' }}>No video: {videoError}</p>
          )}
          <div className="video-container" style={{ position: 'relative',
              background: '#000', borderRadius: '4px', overflow: 'hidden' }}>
            <video
              id="game-video"
              ref={videoRef}
              crossOrigin="anonymous"
              preload="auto"
              muted
              src={videoUrl ?? undefined}
              style={{ display: videoUrl ? 'block' : 'none', width: '100%' }}
            />
            {!videoUrl && (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: '#555',
                            background: '#111' }}>
                No video loaded
              </div>
            )}
          </div>

          {/* Below the video: spatial view beside the event map */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '4px', marginTop: '4px', alignItems: 'start' }}>
          <div style={{ borderRadius: '4px', background: '#1a1a1e',
                        position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', padding: '3px 10px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          fontSize: '11px', color: '#888' }}>
              <span>Spatial View</span>
              <button onClick={spatialPopout ? dockSpatial : popOutSpatial}
                      style={{ background: 'none', border: 'none', color: '#888',
                               fontSize: '11px', cursor: 'pointer' }}>
                {spatialPopout ? '\u2715 Dock' : '\u29C9 Pop out'}
              </button>
            </div>
            {spatialPopout && (
              <div style={{ aspectRatio: '109 / 72', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            color: '#777', fontSize: '12px' }}>
                In its own window
              </div>
            )}
            {/* The host holds ONLY the spatial column: popping out moves
                that node (canvas and all) into the other window, so React
                must never need a sibling to insert it against. */}
            <div ref={spatialHostRef}>
              {/* Docked: the event map's shape (pitch + buffer, 109 x 72)
                  so the two pitches draw at the same size side by side. */}
              <div ref={spatialColRef} className="spatial-column"
                   style={{ position: 'relative',
                            height: spatialPopout ? '100vh' : undefined,
                            aspectRatio: spatialPopout ? undefined : '109 / 72' }}>
                <canvas
                  id="spatial-canvas"
                  style={{ cursor: 'pointer', width: '100%', height: '100%',
                           display: 'block' }}
                  onClick={(e) => controller?.handlePitchClick(e.clientX, e.clientY)}
                />
                <div className="spatial-placeholder" id="spatial-placeholder"
                     style={{ position: 'absolute', inset: 0, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              color: '#555', fontSize: '12px',
                              pointerEvents: 'none' }}>
                  {!controller ? 'Loading spatial data...' : ''}
                </div>
                {/* Shown by the render loop while the playhead's minute
                    loads; never React-updated, so the loop may toggle it. */}
                <div data-role="spatial-loading"
                     style={{ position: 'absolute', inset: 0, display: 'none',
                              alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(14,17,22,0.6)', color: '#ccc',
                              fontSize: '12px', pointerEvents: 'none' }}>
                  Loading tracking{'\u2026'}
                </div>
              </div>
            </div>
          </div>
          <MatchLabPitch
            events={liveEvents}
            periods={info?.periods ?? []}
            directions={info?.video_directions}
            homeTeamId={homeTeamId}
            homeColor={homeColor}
            awayColor={awayColor}
            homeName={info?.home_team ?? 'Home'}
            awayName={info?.away_team ?? 'Away'}
            currentFrame={currentFrame}
            fps={fps}
            onSeek={seekToFrame}
          />
          </div>

          {/* Keyboard shortcut hint */}
          <div style={{ marginTop: '4px', fontSize: '11px', color: '#999',
                        textAlign: 'center' }}>
            Press <kbd style={{ background: '#eee', padding: '0 4px', borderRadius: '2px',
                                border: '1px solid #ccc' }}>?</kbd> for keyboard shortcuts
          </div>
        </div>

        {/* RIGHT: labels + audit trail (vertical) */}
        <div style={{ fontSize: '11px' }}>
          {/* Label controls */}
          {/* Title on its own line; filters and + Label share the next,
              sized to the 280px column rather than overflowing it. */}
          <div style={{ marginBottom: '4px' }}>
            <div className="t-label" style={{ fontSize: '11px',
                                              marginBottom: '3px' }}>
              Events ({shownRows.length})
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {logRows.length > 0 && (
                <select className="adm-select" aria-label="Event source"
                        style={{ fontSize: '10px', padding: '1px 4px',
                                 flex: '1 1 0', minWidth: 0, width: 'auto' }}
                        value={auditSource}
                        onChange={(e) => setAuditSource(
                          e.target.value as 'all' | 'label' | 'detected')}>
                  <option value="all">All</option>
                  <option value="detected">Detected</option>
                  <option value="label">Labels</option>
                </select>
              )}
              {logRows.length > 0 && (
                <select className="adm-select" aria-label="Event type"
                        style={{ fontSize: '10px', padding: '1px 4px',
                                 flex: '1 1 0', minWidth: 0, width: 'auto' }}
                        value={auditFilter}
                        onChange={(e) => setAuditFilter(e.target.value)}>
                  <option value="">All types</option>
                  {[...new Set(logRows.map((r) => r.type).filter(Boolean))].sort().map((t) => (
                    <option key={t} value={t!}>{t}</option>
                  ))}
                </select>
              )}
              <button className="adm-btn"
                      style={{ fontSize: '10px', padding: '1px 6px',
                               flexShrink: 0, whiteSpace: 'nowrap',
                               marginLeft: 'auto' }}
                      onClick={() => setLabelDropdownOpen(!labelDropdownOpen)}>
                {labelDropdownOpen ? '\u2715' : '+ Label'}
              </button>
            </div>
          </div>

          {lastLabel && (
            <div style={{ fontSize: '10px', padding: '3px 6px', borderRadius: '3px',
                          marginBottom: '4px',
                          background: lastLabel.startsWith('ERROR')
                            ? 'rgba(229,62,62,0.1)' : 'rgba(56,161,105,0.1)',
                          color: lastLabel.startsWith('ERROR') ? '#e53e3e' : '#38a169' }}>
              {lastLabel}
            </div>
          )}

          {/* Label form (inline dropdown) */}
          {labelDropdownOpen && (
            <div style={{ marginBottom: '6px' }}>
              <LabelForm
                gameId={gameId}
                frame={currentFrame}
                homeTeamId={homeTeamId}
                awayTeamId={awayTeamId}
                homeName={info?.home_team ?? 'Home'}
                awayName={info?.away_team ?? 'Away'}
                eventTypes={labels?.event_types ?? []}
                onCreated={() => { loadLabels(); setLabelDropdownOpen(false) }}
              />
            </div>
          )}

          {labelsError && <p className="error" style={{ fontSize: '11px' }}>{labelsError}</p>}

          {/* Audit trail (scrollable vertical list) */}
          <div style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto',
                        border: '1px solid var(--color-border, #eee)',
                        borderRadius: '4px' }}>
            {shownRows.length === 0 && (
              <div style={{ padding: '16px 8px', color: '#999', textAlign: 'center' }}>
                No events yet. Use keyboard shortcuts or + Label.
              </div>
            )}
            {shownRows.map((r) => {
              const teamName = r.team_id === homeTeamId
                ? (info?.home_team ?? 'Home')
                : r.team_id === awayTeamId
                  ? (info?.away_team ?? 'Away') : null
              const minute = info?.periods
                ? frameToMinute(r.frame, fps, info.periods) : `f${r.frame}`
              const elapsed = info?.periods
                ? frameToElapsed(r.frame, fps, info.periods) : ''
              return (
                <div key={r.key} data-source={r.source}
                     style={{ display: 'flex', gap: '6px', padding: '4px 6px',
                              borderBottom: '1px solid var(--color-border, #f0f0f0)',
                              cursor: 'pointer', alignItems: 'center',
                              lineHeight: 1.3 }}
                     onClick={() => seekAndPlay(r.frame)}
                     title={`Click to play from f${r.frame}${r.notes ? ` — ${r.notes}` : ''}`}>
                  {r.team_id != null && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%',
                                   display: 'inline-block', flexShrink: 0,
                                   background: r.team_id === homeTeamId
                                     ? homeColor : awayColor }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0,
                                opacity: r.event?.review?.action === 'reject' ? 0.55 : 1,
                                textDecoration: r.event?.review?.action === 'reject'
                                  ? 'line-through' : undefined }}>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {r.type && (
                        <span style={{ background: '#2a2a2e', color: '#e8e8e8',
                                       borderRadius: '2px', padding: '0 4px',
                                       fontSize: '10px', fontWeight: 600 }}>
                          {r.type}
                        </span>
                      )}
                      {r.source === 'detected' && (
                        <span title="Detected by the L4 event builder"
                              style={{ fontSize: '9px', opacity: 0.5 }}>L4</span>
                      )}
                      {r.event && <ReviewBadge event={r.event} />}
                      {teamName && (
                        <span style={{ fontSize: '10px', opacity: 0.6 }}>{teamName}</span>
                      )}
                    </div>
                    {r.notes && (
                      <div style={{ fontSize: '10px', opacity: 0.5,
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap' }}>
                        {r.notes}
                      </div>
                    )}
                    {r.event && (
                      <div style={{ textDecoration: 'none', display: 'inline-block' }}>
                        <ReviewControls
                          event={r.event}
                          mode={reviewing?.key === r.event.key ? reviewing.mode : null}
                          onMode={(m) => setReviewing(
                            m ? { key: r.event!.key, mode: m } : null)}
                          onSubmit={(body) => { void reviewEvent(r.event!, body) }}
                          currentFrame={currentFrame}
                          homeTeamId={homeTeamId}
                          awayTeamId={awayTeamId}
                          homeName={info?.home_team ?? 'Home'}
                          awayName={info?.away_team ?? 'Away'}
                        />
                      </div>
                    )}
                  </div>
                  {r.labelId != null && (
                    <span style={{ display: 'flex', gap: '2px', flexShrink: 0 }}
                          onClick={(e) => e.stopPropagation()}>
                      {(r.side === 'home' || r.side === 'away') && (
                        <button
                          title={`Reassign to ${r.side === 'home'
                            ? (info?.away_team ?? 'away')
                            : (info?.home_team ?? 'home')}`}
                          onClick={() => flipLabel(
                            r.labelId!, r.side === 'home' ? 'away' : 'home')}
                          style={{ background: 'none', border: '1px solid #444',
                                   borderRadius: '2px', color: 'inherit',
                                   cursor: 'pointer', fontSize: '10px',
                                   lineHeight: 1.2, padding: '0 3px' }}>
                          ⇄
                        </button>
                      )}
                      <button
                        title="Delete this label"
                        onClick={() => removeLabel(r.labelId!)}
                        style={{ background: 'none', border: '1px solid #444',
                                 borderRadius: '2px', color: 'inherit',
                                 cursor: 'pointer', fontSize: '10px',
                                 lineHeight: 1.2, padding: '0 3px' }}>
                        ×
                      </button>
                    </span>
                  )}
                  <div style={{ textAlign: 'right', flexShrink: 0,
                                fontVariantNumeric: 'tabular-nums' }}>
                    <div style={{ fontWeight: 500 }}>{minute}</div>
                    {elapsed && (
                      <div style={{ fontSize: '9px', opacity: 0.5 }}>{elapsed}</div>
                    )}
                    <div style={{ fontSize: '9px', opacity: 0.3 }}>f{r.frame}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Event timeline bar ──

/** A small soccer ball — white with the classic black pentagon-and-seams —
 *  outlined in the scoring team's colour, so a goal reads as a goal on the
 *  timeline rather than as one more coloured dot. Pentagon and seams are
 *  computed from a regular pentagon so the pattern stays true at any size. */
function GoalBall({ color, size = 14 }: { color: string; size?: number }) {
  const cx = 10, cy = 10, rPent = 3.2, rSeam = 6.6
  const verts: { x: number; y: number; ox: number; oy: number }[] = []
  for (let k = 0; k < 5; k++) {
    const a = ((-90 + 72 * k) * Math.PI) / 180
    verts.push({
      x: cx + rPent * Math.cos(a), y: cy + rPent * Math.sin(a),
      ox: cx + rSeam * Math.cos(a), oy: cy + rSeam * Math.sin(a),
    })
  }
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" role="img"
         aria-label="Goal">
      <circle cx={cx} cy={cy} r={7.5} fill="#fff" stroke={color} strokeWidth={2} />
      {verts.map((v, i) => (
        <line key={i} x1={v.x} y1={v.y} x2={v.ox} y2={v.oy}
              stroke="#1a1a1a" strokeWidth={0.9} />
      ))}
      <polygon points={verts.map((v) => `${v.x.toFixed(2)},${v.y.toFixed(2)}`).join(' ')}
               fill="#1a1a1a" />
    </svg>
  )
}

export function EventTimeline({ events, periods, totalFrames, trackedFrames,
                         currentFrame, fps,
                         homeColor, awayColor, homeTeamId, onSeek }: {
  events: TimelineEvent[]
  /** The bar's domain — see timelineDomain; >= trackedFrames. */
  totalFrames: number
  /** Where tracking ends; the stretch beyond it is shaded darker. */
  trackedFrames: number
  periods: Period[]
  currentFrame: number
  fps: number
  homeColor: string
  awayColor: string
  homeTeamId: number | null
  onSeek: (frame: number) => void
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const BAR_HEIGHT = 32

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = barRef.current
    if (!bar || totalFrames <= 0) return
    const rect = bar.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    onSeek(Math.round(pct * totalFrames))
  }, [totalFrames, onSeek])

  const scrubberPct = totalFrames > 0
    ? Math.min(100, (currentFrame / totalFrames) * 100) : 0

  const minuteLabels: { label: string; pct: number }[] = useMemo(() => {
    const out: { label: string; pct: number }[] = []
    if (periods.length === 0 || fps <= 0) return out
    out.push({ label: 'KO', pct: 0 })
    for (let m = 15; m <= 45; m += 15) {
      const frame = periods[0]!.start_frame_idx + m * 60 * fps
      if (frame < totalFrames)
        out.push({ label: `${m}'`, pct: (frame / totalFrames) * 100 })
    }
    if (periods.length >= 2) {
      out.push({
        label: 'HT',
        pct: ((periods[0]!.end_frame_idx + periods[1]!.start_frame_idx) / 2 / totalFrames) * 100,
      })
      for (let m = 15; m <= 45; m += 15) {
        const frame = periods[1]!.start_frame_idx + m * 60 * fps
        if (frame < totalFrames)
          out.push({ label: `${m + 45}'`, pct: (frame / totalFrames) * 100 })
      }
    }
    // FT sits where TRACKING ends, not at the bar's edge: the domain may
    // extend past it (stoppage played or labeled beyond the feed).
    out.push({ label: 'FT',
               pct: Math.min(100, (trackedFrames / totalFrames) * 100) })
    return out
  }, [periods, fps, totalFrames, trackedFrames])

  return (
    <div style={{ marginTop: '6px' }}>
      <div style={{ position: 'relative', height: '14px', fontSize: '9px',
                    color: '#666', userSelect: 'none' }}>
        {minuteLabels.map((ml, i) => (
          <span key={i} style={{
            position: 'absolute', left: `${ml.pct}%`,
            transform: 'translateX(-50%)', whiteSpace: 'nowrap',
          }}>{ml.label}</span>
        ))}
      </div>

      <div ref={barRef}
           style={{ position: 'relative', height: BAR_HEIGHT,
                    background: '#1a1a1e', borderRadius: '3px',
                    cursor: 'pointer', overflow: 'hidden' }}
           onClick={handleClick}>

        {trackedFrames < totalFrames && (
          <div title="Beyond the tracking feed — video and labels only"
               style={{
                 position: 'absolute',
                 left: `${(trackedFrames / totalFrames) * 100}%`,
                 right: 0, top: 0, bottom: 0,
                 background:
                   'repeating-linear-gradient(-45deg, rgba(0,0,0,0.35) 0 4px, transparent 4px 8px)',
               }} />
        )}
        {periods.map((p) => {
          const left = (p.start_frame_idx / totalFrames) * 100
          const width = ((p.end_frame_idx - p.start_frame_idx) / totalFrames) * 100
          return (
            <div key={p.period_id} style={{
              position: 'absolute', left: `${left}%`, width: `${width}%`,
              top: 0, bottom: 0,
              background: p.period_id % 2 === 1 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
            }} />
          )
        })}

        {periods.length >= 2 && (
          <div style={{
            position: 'absolute',
            left: `${(periods[0]!.end_frame_idx / totalFrames) * 100}%`,
            top: 0, bottom: 0, width: '2px', background: 'rgba(255,255,255,0.2)',
          }} />
        )}

        {events.map((evt, i) => {
          const pct = (evt.frame_idx / totalFrames) * 100
          const isGoal = evt.type === 'goal'
          const color = evt.team_id === homeTeamId ? homeColor : awayColor
          if (evt.type === 'set_piece') {
            return (
              <div key={i} data-kind="set_piece"
                   title={`${evt.label} at f${evt.frame_idx}`} style={{
                position: 'absolute', left: `${pct}%`, top: 22,
                width: 5, height: 5, background: color, opacity: 0.8,
                transform: 'translateX(-50%) rotate(45deg)', zIndex: 1,
                cursor: 'pointer',
              }}
              onClick={(e) => { e.stopPropagation(); onSeek(eventSeekFrame(evt.type, evt.frame_idx, fps)) }}
              />
            )
          }
          if (isGoal) {
            // A goal is a soccer ball ringed in the scoring team's colour.
            return (
              <div key={i} data-kind="goal"
                   title={`${evt.label} at f${evt.frame_idx}`} style={{
                position: 'absolute', left: `${pct}%`, top: 1,
                transform: 'translateX(-50%)', zIndex: 3, lineHeight: 0,
                cursor: 'pointer',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
              }}
              onClick={(e) => { e.stopPropagation(); onSeek(eventSeekFrame(evt.type, evt.frame_idx, fps)) }}>
                <GoalBall color={color} />
              </div>
            )
          }
          return (
            <div key={i} data-kind={evt.type}
                 title={`${evt.label} at f${evt.frame_idx}`} style={{
              position: 'absolute', left: `${pct}%`, top: 10,
              width: 6, height: 6, borderRadius: '2px',
              background: color,
              border: '1px solid rgba(255,255,255,0.7)',
              transform: 'translateX(-50%)', zIndex: 2,
              cursor: 'pointer',
            }}
            onClick={(e) => { e.stopPropagation(); onSeek(eventSeekFrame(evt.type, evt.frame_idx, fps)) }}
            />
          )
        })}

        <div style={{
          position: 'absolute', left: `${scrubberPct}%`,
          top: 0, bottom: 0, width: '2px', background: '#fff', zIndex: 5,
          pointerEvents: 'none',
        }} />
      </div>
    </div>
  )
}

// ── Label creation form (dropdown) ──

function LabelForm({ gameId, frame, homeTeamId, awayTeamId, homeName, awayName,
                     eventTypes, onCreated }: {
  gameId: number
  frame: number
  homeTeamId: number | null
  awayTeamId: number | null
  homeName: string
  awayName: string
  eventTypes: string[]
  onCreated: () => void
}) {
  const [eventType, setEventType] = useState(ALL_EVENT_TYPES[0]!)
  const [teamId, setTeamId] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showTypeHelp, setShowTypeHelp] = useState(false)

  const types = [...new Set([...ALL_EVENT_TYPES, ...eventTypes])].sort()

  const submit = useCallback(async () => {
    setSubmitting(true); setSubmitError(null)
    try {
      if (teamId === 'both') {
        // Create one label per team
        for (const tid of [homeTeamId, awayTeamId]) {
          if (tid == null) continue
          const body: Record<string, unknown> = {
            frame, event_type: eventType,
            side: toLabelSide(tid, homeTeamId, awayTeamId),
          }
          if (notes.trim()) body.notes = notes.trim()
          await api(`/api/v1/opsqa/games/${gameId}/labels`,
            { method: 'POST', body: JSON.stringify(body) })
        }
      } else {
        const body: Record<string, unknown> = { frame, event_type: eventType }
        const side = teamId === '' ? null
          : toLabelSide(Number(teamId), homeTeamId, awayTeamId)
        if (side != null) body.side = side
        if (notes.trim()) body.notes = notes.trim()
        await api(`/api/v1/opsqa/games/${gameId}/labels`,
          { method: 'POST', body: JSON.stringify(body) })
      }
      setSubmitting(false)
      setNotes('')
      onCreated()
    } catch (e: unknown) {
      setSubmitError(e instanceof ApiError ? e.message : String(e))
      setSubmitting(false)
    }
  }, [gameId, frame, eventType, teamId, homeTeamId, awayTeamId, notes, onCreated])

  return (
    <div style={{ padding: '8px', border: '1px solid var(--color-border, #ccc)',
                  borderRadius: '4px', background: 'var(--color-surface-raised, #fafafa)',
                  display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span className="t-label" style={{ fontSize: '11px' }}>
        Label at frame <strong>f{frame}</strong>
      </span>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center',
                    position: 'relative' }}>
        <select className="adm-select" style={{ fontSize: '11px', flex: 1 }}
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}>
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button onClick={() => setShowTypeHelp(!showTypeHelp)}
                style={{ background: 'none', border: '1px solid #ccc',
                         borderRadius: '50%', width: '18px', height: '18px',
                         fontSize: '11px', cursor: 'pointer', color: '#666',
                         display: 'flex', alignItems: 'center',
                         justifyContent: 'center', flexShrink: 0 }}
                title="Show event type descriptions">?</button>
        {showTypeHelp && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
                        zIndex: 10, marginTop: '2px',
                        background: '#1a1a1e', color: '#ddd',
                        borderRadius: '4px', padding: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                        maxHeight: '240px', overflowY: 'auto',
                        fontSize: '10px', lineHeight: 1.5 }}>
            {types.map((t) => (
              <div key={t} style={{ padding: '2px 0',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    cursor: 'pointer',
                                    fontWeight: t === eventType ? 600 : 400 }}
                   onClick={() => { setEventType(t); setShowTypeHelp(false) }}>
                <strong style={{ color: '#fff' }}>{t}</strong>
                {EVENT_TYPE_DESCRIPTIONS[t] && (
                  <span style={{ opacity: 0.6, marginLeft: '6px' }}>
                    {EVENT_TYPE_DESCRIPTIONS[t]}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {EVENT_TYPE_DESCRIPTIONS[eventType] && (
        <div style={{ fontSize: '10px', opacity: 0.5, marginTop: '-2px' }}>
          {EVENT_TYPE_DESCRIPTIONS[eventType]}
        </div>
      )}
      <select className="adm-select" style={{ fontSize: '11px' }}
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}>
        <option value="">No team</option>
        {homeTeamId != null && <option value={String(homeTeamId)}>{homeName} (Home)</option>}
        {awayTeamId != null && <option value={String(awayTeamId)}>{awayName} (Away)</option>}
        <option value="both">Both teams</option>
      </select>
      <input className="adm-input" style={{ fontSize: '11px' }}
             value={notes}
             onChange={(e) => setNotes(e.target.value)}
             placeholder="Optional note"
             onKeyDown={(e) => { if (e.key === 'Enter') submit() }} />
      <button className="adm-btn" style={{ fontSize: '11px' }}
              disabled={submitting}
              onClick={submit}>
        {submitting ? 'Saving...' : 'Save label'}
      </button>
      {submitError && <span style={{ color: 'var(--color-danger, #e53e3e)',
                                     fontSize: '11px' }}>{submitError}</span>}
    </div>
  )
}
